import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonBase,
  Chip,
  Collapse,
  CircularProgress,
  Divider,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import FiberNewOutlinedIcon from '@mui/icons-material/FiberNewOutlined';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import { alpha } from '@mui/material/styles';
import {
  Timestamp,
  collection,
  collectionGroup,
  doc,
  documentId,
  getCountFromServer,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { useAuth, usePermissions } from '../AuthContext';
import AddLeadDrawer from '../components/AddLeadDrawer';
import LeadCompactCard from '../components/LeadCompactCard';
import LeadDetailDrawer from '../components/LeadDetailDrawer';
import LeadAssignmentStatusPanel from '../components/LeadAssignmentStatusPanel';
import LeadVisitAnalyticsPanel, { monthDailyMonthOptionsList } from '../components/LeadVisitAnalyticsPanel';

export default function DashboardHomePage() {
  const { currentUser, roleId } = useAuth();
  const { canAction } = usePermissions();
  const db = useMemo(() => getFirestore(), []);

  const isAdminish = useMemo(
    () => roleId === 'super-admin' || roleId === 'admin' || roleId === 'sales-manager',
    [roleId]
  );
  const isTopAdmin = useMemo(() => roleId === 'super-admin' || roleId === 'admin', [roleId]);
  const isSalesManager = useMemo(() => roleId === 'sales-manager', [roleId]);
  const isSalesTeam = useMemo(() => roleId === 'sales-team', [roleId]);
  const canViewTeamSnapshot = useMemo(() => Boolean(currentUser), [currentUser]);
  const canViewSalesJourney = useMemo(() => isTopAdmin || isSalesManager, [isSalesManager, isTopAdmin]);

  const [error, setError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [detailLeadId, setDetailLeadId] = useState<string | null>(null);
  const [detailLeadFallback, setDetailLeadFallback] = useState<any | null>(null);
  const [detailWidth, setDetailWidth] = useState(460);
  const [dashboardSection, setDashboardSection] = useState<string>('');

  const [leadStatuses, setLeadStatuses] = useState<Array<{ id: string; label?: string | null; name?: string | null; color?: string | null }>>(
    []
  );
  const [userDirectory, setUserDirectory] = useState<
    Array<{ id: string; name?: string | null; email?: string | null; roleId?: string | null; role?: string | null }>
  >(
    []
  );

  const [todayDue, setTodayDue] = useState<any[]>([]);
  const [overdue, setOverdue] = useState<any[]>([]);
  const [scheduledToday, setScheduledToday] = useState<any[]>([]);
  const [newToday, setNewToday] = useState<any[]>([]);
  const [unassignedLeads, setUnassignedLeads] = useState<any[]>([]);
  const [dueTodayOpen, setDueTodayOpen] = useState(false);
  const [overdueOpen, setOverdueOpen] = useState(false);
  const [scheduledTodayOpen, setScheduledTodayOpen] = useState(false);
  const [newLeadsOpen, setNewLeadsOpen] = useState(false);
  const [unassignedOpen, setUnassignedOpen] = useState(false);

  const [countNewToday, setCountNewToday] = useState<number | null>(null);
  const [countDueToday, setCountDueToday] = useState<number | null>(null);
  const [countOverdue, setCountOverdue] = useState<number | null>(null);
  const [countApptToday, setCountApptToday] = useState<number | null>(null);
  const [countClosed7d, setCountClosed7d] = useState<number | null>(null);
  const [countUnassigned, setCountUnassigned] = useState<number | null>(null);
  const [avgDaysToCloseCompleted7d, setAvgDaysToCloseCompleted7d] = useState<number | null>(null);
  const [pipelineSnapshot, setPipelineSnapshot] = useState<
    Array<{ key: string; label: string; count: number | null; trend7d: number | null; color: any }>
  >([]);
  const [pipelineClosedReasonCounts, setPipelineClosedReasonCounts] = useState<Record<string, number | null>>({});
  const [funnelOverall, setFunnelOverall] = useState<{
    newCount: number | null;
    connectedCount: number | null;
    visitCount: number | null;
    closedCount: number | null;
    completedCount: number | null;
  }>({ newCount: null, connectedCount: null, visitCount: null, closedCount: null, completedCount: null });
  const [funnelBySource, setFunnelBySource] = useState<
    Array<{
      key: string;
      label: string;
      newCount: number;
      connectedCount: number;
      visitCount: number;
      closedCount: number;
      completedCount: number;
    }>
  >([]);
  const [, setFunnelByProject] = useState<
    Array<{
      key: string;
      label: string;
      newCount: number;
      connectedCount: number;
      visitCount: number;
      closedCount: number;
      completedCount: number;
    }>
  >([]);
  const [funnelByAgent, setFunnelByAgent] = useState<
    Array<{
      key: string;
      label: string;
      newCount: number;
      connectedCount: number;
      visitCount: number;
      closedCount: number;
      completedCount: number;
    }>
  >([]);
  const [kpiError, setKpiError] = useState<string | null>(null);
  const [sourcePerfRows, setSourcePerfRows] = useState<
    Array<{
      key: string;
      label: string;
      newCount: number;
      closedCount: number;
      conversionPct: number | null;
      avgDaysToClose: number | null;
      apptCount: number;
      noShowCount: number;
      noShowPct: number | null;
    }>
  >([]);
  const [, setTopBrokers] = useState<Array<{ key: string; label: string; leads: number }>>([]);
  const [brokerAnalyticsRows, setBrokerAnalyticsRows] = useState<
    Array<{ key: string; label: string; total: number; closed: number; completed: number; leads: any[] }>
  >([]);
  const [brokerRange, setBrokerRange] = useState<'7d' | '30d' | 'open'>('30d');
  const [brokerExpandedKey, setBrokerExpandedKey] = useState<string | null>(null);
  const [brokerVisibleCount, setBrokerVisibleCount] = useState<Record<string, number>>({});
  const [agentPerfRows, setAgentPerfRows] = useState<
    Array<{
      key: string;
      label: string;
      callsConnected: number;
      callsTotal: number;
      visits: number;
      internal: number;
      leadsTouched: number;
      closures: number;
      overdueSharePct: number | null;
      avgFirstResponseMins: number | null;
    }>
  >([]);
  const [riskHighOverdue, setRiskHighOverdue] = useState<any[]>([]);
  const [riskNoActivityBuckets, setRiskNoActivityBuckets] = useState<{ d3: number | null; d7: number | null; d14: number | null }>({
    d3: null,
    d7: null,
    d14: null,
  });
  const [riskNoActivityLeads, setRiskNoActivityLeads] = useState<any[]>([]);
  const [riskHotNoFollowUpLeads, setRiskHotNoFollowUpLeads] = useState<any[]>([]);

  const [teamTodayByAssignee, setTeamTodayByAssignee] = useState<Record<string, number>>({});
  const [teamOverdueByAssignee, setTeamOverdueByAssignee] = useState<Record<string, number>>({});
  const [teamRange, setTeamRange] = useState<'today' | 'yesterday' | '7d'>('today');
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState<string | null>(null);
  const [teamRecentByUser, setTeamRecentByUser] = useState<
    Record<
      string,
      Array<{
        id: string;
        leadId: string;
        at: any;
        type?: string | null;
        event?: string | null;
        callOutcome?: string | null;
        visitWith?: string | null;
        notes?: string[] | null;
        statusFrom?: string | null;
        statusTo?: string | null;
        followUpFrom?: any;
        followUpDate?: any;
        byEmail?: string | null;
      }>
    >
  >({});
  const [teamStats, setTeamStats] = useState<
    Array<{
      key: string;
      name: string;
      callsConnected: number;
      callsTotal: number;
      visits: number;
      internal: number;
      leadsTouched: number;
    }>
  >([]);
  const [workDoneUserKey, setWorkDoneUserKey] = useState<string | null>(null);
  const [workDoneVisible, setWorkDoneVisible] = useState(10);
  const [workDoneExpanded, setWorkDoneExpanded] = useState(false);
  const [teamMetricOpen, setTeamMetricOpen] = useState<{ userKey: string; metric: string } | null>(null);
  const [teamMetricVisible, setTeamMetricVisible] = useState(15);
  const [journeyRange, setJourneyRange] = useState<'today' | '7d' | '30d' | 'month'>('30d');
  const [journeySalesperson, setJourneySalesperson] = useState<string>('all');
  const [journeySource, setJourneySource] = useState<string>('all');
  const [journeyLoading, setJourneyLoading] = useState(false);
  const [journeyError, setJourneyError] = useState<string | null>(null);
  const [journeyTree, setJourneyTree] = useState<any | null>(null);
  const [journeySiteVisitedCount, setJourneySiteVisitedCount] = useState(0);
  const [journeyLeadCache, setJourneyLeadCache] = useState<Record<string, any>>({});
  const [journeySourceOptions, setJourneySourceOptions] = useState<string[]>([]);
  const [journeyViewMode, setJourneyViewMode] = useState<'paths' | 'hierarchy'>(() => {
    if (typeof window === 'undefined') return 'paths';
    const stored = window.localStorage.getItem('salesJourneyViewMode');
    return stored === 'hierarchy' || stored === 'paths' ? stored : 'paths';
  });
  const [journeyExpanded, setJourneyExpanded] = useState<Record<string, boolean>>({});
  const [journeyOpenBranch, setJourneyOpenBranch] = useState<{ pathKey: string; label: string; leadIds: string[] } | null>(null);
  const [journeyBranchVisible, setJourneyBranchVisible] = useState(20);
  const journeyHierarchyCanvasRef = useRef<HTMLDivElement | null>(null);
  const [journeyConnectors, setJourneyConnectors] = useState<Array<{ id: string; d: string; color: string; markerId: string; active: boolean }>>([]);
  const [journeyConnectorSize, setJourneyConnectorSize] = useState({ width: 0, height: 0 });

  const currentMonthKey = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, []);
  const [closeAnalysisMonthKey, setCloseAnalysisMonthKey] = useState<string>(currentMonthKey);
  const [closeAnalysisLoading, setCloseAnalysisLoading] = useState(false);
  const [closeAnalysisError, setCloseAnalysisError] = useState<string | null>(null);
  const [closeAnalysisSummary, setCloseAnalysisSummary] = useState<{
    totalClosed: number;
    completed: number;
    lost: number;
  }>({ totalClosed: 0, completed: 0, lost: 0 });
  const [closeAnalysisView, setCloseAnalysisView] = useState<'summary' | 'daily'>('summary');
  const [closeAnalysisPrimaryDetail, setCloseAnalysisPrimaryDetail] = useState<Record<string, Record<string, number>>>({});
  const [closeAnalysisStageDetail, setCloseAnalysisStageDetail] = useState<Record<string, Record<string, number>>>({});
  const [closeAnalysisBudgetDetail, setCloseAnalysisBudgetDetail] = useState<Record<string, Record<string, number>>>({});
  const [closeAnalysisCompetitorDetail, setCloseAnalysisCompetitorDetail] = useState<Record<string, Record<string, number>>>({});
  const [closeAnalysisSubDetail, setCloseAnalysisSubDetail] = useState<
    Record<string, Record<string, Record<string, number>>>
  >({});
  const [closeAnalysisDailyDetail, setCloseAnalysisDailyDetail] = useState<
    Record<string, Record<string, Record<string, number>>>
  >({});
  const [closeAnalysisDailyRows, setCloseAnalysisDailyRows] = useState<
    Array<{ key: string; totalClosed: number; [reason: string]: any }>
  >([]);
  const [closeAnalysisLeadCache, setCloseAnalysisLeadCache] = useState<Record<string, any>>({});
  const [closeAnalysisOpen, setCloseAnalysisOpen] = useState<{
    group: 'primary' | 'stage' | 'budget' | 'competitor';
    key: string;
  } | null>(null);
  const [closeAnalysisOpenSourceKey, setCloseAnalysisOpenSourceKey] = useState<string | null>(null);
  const [closeAnalysisDailyOpen, setCloseAnalysisDailyOpen] = useState<{
    dayKey: string;
    reason: string;
  } | null>(null);

  const ymdKey = useCallback((d: Date) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const toDateMaybe = useCallback((v: any): Date | null => {
    if (!v) return null;
    if (v?.toDate) {
      const d = v.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    }
    if (v instanceof Date) return !Number.isNaN(v.getTime()) ? v : null;
    if (typeof v === 'string') {
      const d = new Date(v);
      return !Number.isNaN(d.getTime()) ? d : null;
    }
    return null;
  }, []);

  const loadCloseAnalysis = useCallback(async (targetMonthKey: string) => {
    if (!isTopAdmin && !isSalesManager) return;
    setCloseAnalysisLoading(true);
    setCloseAnalysisError(null);
    try {
      const [yearStr, monthStr] = String(targetMonthKey || '').split('-');
      const year = Number(yearStr);
      const monthIdx = Number(monthStr) - 1;
      if (!Number.isFinite(year) || !Number.isFinite(monthIdx) || monthIdx < 0 || monthIdx > 11) {
        throw new Error('Invalid month selected.');
      }
      const start = new Date(year, monthIdx, 1);
      start.setHours(0, 0, 0, 0);
      const end = new Date(year, monthIdx + 1, 1);
      end.setHours(0, 0, 0, 0);
      const startTs = Timestamp.fromDate(start);
      const endTs = Timestamp.fromDate(end);

      const leadMap = new Map<string, any>();
      const includedLeadIds = new Set<string>();
      const primaryDetail: Record<string, Record<string, number>> = {};
      const stageDetail: Record<string, Record<string, number>> = {};
      const budgetDetail: Record<string, Record<string, number>> = {};
      const competitorDetail: Record<string, Record<string, number>> = {};
      const subDetail: Record<string, Record<string, Record<string, number>>> = {};
      const dailyDetail: Record<string, Record<string, Record<string, number>>> = {};
      let completed = 0;
      let lost = 0;
      const reasonColumns = [
        'Completed',
        'Not Interested',
        'Interested in Another Location',
        'Budget / Financial Issue',
        'Distance Issue',
        'Not Reachable (No Response)',
        'Wrong / Invalid Number',
        'Broker Inquiry Only',
        'Loss due to Competitor',
        'Other',
      ] as const;

      const bump = (bucket: Record<string, Record<string, number>>, key: string, leadId: string) => {
        const k = String(key || '').trim() || 'Unknown';
        const row = (bucket[k] = bucket[k] || {});
        row[leadId] = (row[leadId] || 0) + 1;
      };
      const bumpSub = (reason: string, subResponse: string, leadId: string) => {
        const reasonKey = String(reason || '').trim() || 'Unknown';
        const subKey = String(subResponse || '').trim();
        if (!subKey || !leadId) return;
        const reasonMap = (subDetail[reasonKey] = subDetail[reasonKey] || {});
        const responseMap = (reasonMap[subKey] = reasonMap[subKey] || {});
        responseMap[leadId] = (responseMap[leadId] || 0) + 1;
      };
      const bumpSubCombined = (
        reason: string,
        parts: Array<{ label: string; value: string | null | undefined }>,
        leadId: string
      ) => {
        const chunks = parts
          .map((p) => ({
            label: String(p?.label || '').trim(),
            value: String(p?.value || '').trim(),
          }))
          .filter((p) => p.label && p.value)
          .map((p) => `${p.label}: ${p.value}`);
        if (!chunks.length) return;
        bumpSub(reason, chunks.join(' + '), leadId);
      };
      const bumpDaily = (dayKey: string, reason: string, leadId: string) => {
        if (!dayKey || !leadId) return;
        const day = (dailyDetail[dayKey] = dailyDetail[dayKey] || {});
        const reasonMap = (day[reason] = day[reason] || {});
        reasonMap[leadId] = (reasonMap[leadId] || 0) + 1;
      };
      const isClosedLikeLocal = (status: any) => {
        const norm = (v: any) => String(v || '').trim().toLowerCase();
        const n = norm(status);
        if (!n) return false;
        if (n === 'closed' || n === 'completed') return true;
        const mapped = leadStatuses.find((s) => String(s?.id || '').trim().toLowerCase() === n);
        const m = norm((mapped as any)?.label || (mapped as any)?.name);
        return m === 'closed' || m === 'completed';
      };
      const normalizeReason = (raw: string) => {
        const s = String(raw || '').trim().toLowerCase();
        if (!s) return 'Other';
        if (s === 'completed') return 'Completed';
        if (s === 'not interested') return 'Not Interested';
        if (s === 'interested in another location') return 'Interested in Another Location';
        if (s === 'budget / financial issue') return 'Budget / Financial Issue';
        if (s === 'distance issue') return 'Distance Issue';
        if (s === 'not reachable (no response)') return 'Not Reachable (No Response)';
        if (s === 'wrong / invalid number') return 'Wrong / Invalid Number';
        if (s === 'broker inquiry only') return 'Broker Inquiry Only';
        if (s === 'loss due to competitor') return 'Loss due to Competitor';
        return 'Other';
      };

      const asStringArray = (v: any): string[] =>
        Array.isArray(v) ? v.filter((x) => typeof x === 'string' && x.trim()).map((x) => String(x).trim()) : [];

      const includeLead = (leadId: string, lead: any, closedDate: Date | null) => {
        if (!leadId || includedLeadIds.has(leadId)) return;
        includedLeadIds.add(leadId);
        leadMap.set(leadId, { id: leadId, ...lead });

        const closedAnalysis = (lead?.closedAnalysis || {}) as any;
        const primaryBase = String(lead?.closePrimaryReason || closedAnalysis?.primaryReasonBase || '').trim();
        const primaryOther = String(lead?.closePrimaryOtherReason || closedAnalysis?.primaryOtherReason || '').trim();
        const resolvedPrimary =
          String(lead?.closeReason || closedAnalysis?.primaryReason || '').trim() ||
          (primaryBase.toLowerCase() === 'other' ? primaryOther : primaryBase);
        const primaryLabel = resolvedPrimary || 'Unknown';
        const normalizedPrimaryLabel = normalizeReason(primaryLabel || primaryBase || '');

        const stageLabel = String(
          lead?.closeDecisionStage || lead?.closeDecisionStageAuto || closedAnalysis?.decisionStage || closedAnalysis?.decisionStageAuto || 'Unknown'
        ).trim() || 'Unknown';
        const budgetLabel = String(lead?.closeBudgetDetail || closedAnalysis?.budgetDetail || '').trim() || 'Unspecified';
        const competitorAdvantageLabel = String(
          lead?.closeCompetitorAdvantage || closedAnalysis?.competitorAdvantage || ''
        ).trim() || 'Unspecified';
        const interestedLocation = String(
          lead?.closeInterestedLocation || closedAnalysis?.interestedLocation || ''
        ).trim();
        const boughtAtLocation = String(
          lead?.closeBoughtAtLocation || closedAnalysis?.boughtAtLocation || ''
        ).trim();
        const requiredType = String(
          lead?.closeRequiredType || closedAnalysis?.requiredType || ''
        ).trim();
        const requiredTypeLocation = String(
          lead?.closeRequiredTypeLocation || closedAnalysis?.requiredTypeLocation || ''
        ).trim();
        const distancePreferredArea = String(
          lead?.closeDistancePreferredArea || closedAnalysis?.distancePreferredArea || ''
        ).trim();
        const competitorProject = String(
          lead?.closeCompetitorProject || closedAnalysis?.competitorProject || ''
        ).trim();
        const competitorPriceBand = String(
          lead?.closeCompetitorPriceBand || closedAnalysis?.competitorPriceBand || ''
        ).trim();
        const secondary = asStringArray(lead?.closeSecondaryReasons || closedAnalysis?.secondaryReasons);
        const primaryKeyLower = String(primaryBase || primaryLabel || '').trim().toLowerCase();

        bump(primaryDetail, primaryLabel, leadId);
        bump(stageDetail, stageLabel, leadId);
        if (closedDate) bumpDaily(ymdKey(closedDate), normalizedPrimaryLabel, leadId);

        if (primaryBase.toLowerCase() === 'budget / financial issue') {
          bump(budgetDetail, budgetLabel, leadId);
        }
        if (
          primaryBase.toLowerCase() === 'loss due to competitor' ||
          secondary.some((s) => String(s).trim().toLowerCase() === 'loss due to competitor')
        ) {
          bump(competitorDetail, competitorAdvantageLabel, leadId);
        }
        if (primaryKeyLower === 'interested in another location' && interestedLocation) {
          bumpSub(primaryLabel, `Interested Location: ${interestedLocation}`, leadId);
        }
        if (primaryKeyLower === 'purchased in another location' && boughtAtLocation) {
          bumpSub(primaryLabel, `Purchased Location: ${boughtAtLocation}`, leadId);
        }
        if (primaryKeyLower === 'need other type') {
          bumpSubCombined(
            primaryLabel,
            [
              { label: 'Required Location', value: requiredTypeLocation },
              { label: 'Required Type', value: requiredType },
            ],
            leadId
          );
        }
        if (primaryKeyLower === 'budget / financial issue' && budgetLabel && budgetLabel !== 'Unspecified') {
          bumpSub(primaryLabel, `Budget Detail: ${budgetLabel}`, leadId);
        }
        if (primaryKeyLower === 'distance issue' && distancePreferredArea) {
          bumpSub(primaryLabel, `Preferred Area: ${distancePreferredArea}`, leadId);
        }
        if (primaryKeyLower === 'loss due to competitor') {
          bumpSubCombined(
            primaryLabel,
            [
              { label: 'Competitor Project', value: competitorProject },
              { label: 'Competitor Price Band', value: competitorPriceBand },
              {
                label: 'Competitor Advantage',
                value: competitorAdvantageLabel !== 'Unspecified' ? competitorAdvantageLabel : '',
              },
            ],
            leadId
          );
        }

        if (primaryBase.toLowerCase() === 'completed' || primaryLabel.toLowerCase() === 'completed') {
          completed += 1;
        } else {
          lost += 1;
        }
      };

      // Primary source: leads closed in selected month by closedAt.
      const closedSnap = await getDocs(
        query(
          collection(db, 'leads'),
          where('closedAt', '>=', startTs),
          where('closedAt', '<', endTs)
        )
      );
      closedSnap.forEach((d) => {
        const lead = d.data() as any;
        includeLead(d.id, lead, toDateMaybe(lead?.closedAt));
      });

      // Fallback: if closedAt is missing, include leads that have a close activity in month.
      const closeActivityLeadIds = new Set<string>();
      const closeActivityAtByLeadId = new Map<string, Date>();
      const activitySnap = await getDocs(
        query(
          collectionGroup(db, 'activities'),
          where('at', '>=', startTs),
          where('at', '<', endTs),
          orderBy('at', 'desc'),
          limit(8000)
        )
      );
      activitySnap.forEach((docSnap) => {
        const a = docSnap.data() as any;
        if (!isClosedLikeLocal(a?.statusTo)) return;
        const leadId = docSnap.ref.parent.parent?.id || '';
        if (!leadId || includedLeadIds.has(leadId)) return;
        closeActivityLeadIds.add(leadId);
        const at = toDateMaybe(a?.at);
        if (at) {
          const prev = closeActivityAtByLeadId.get(leadId);
          if (!prev || at.getTime() < prev.getTime()) closeActivityAtByLeadId.set(leadId, at);
        }
      });
      const fallbackIds = Array.from(closeActivityLeadIds);
      for (let i = 0; i < fallbackIds.length; i += 10) {
        const batchIds = fallbackIds.slice(i, i + 10);
        // eslint-disable-next-line no-await-in-loop
        const leadSnap = await getDocs(query(collection(db, 'leads'), where(documentId(), 'in', batchIds)));
        leadSnap.forEach((d) => {
          const lead = d.data() as any;
          const closedAt = toDateMaybe(lead?.closedAt);
          // Fallback applies only when closedAt is absent.
          if (!closedAt) includeLead(d.id, lead, closeActivityAtByLeadId.get(d.id) || null);
        });
      }

      const dailyRows = Object.entries(dailyDetail)
        .map(([dayKey, reasonMap]) => {
          const row: { key: string; totalClosed: number; [reason: string]: any } = {
            key: dayKey,
            totalClosed: 0,
          };
          reasonColumns.forEach((reason) => {
            const count = Object.values(reasonMap?.[reason] || {}).reduce(
              (a, b) => a + (Number(b) || 0),
              0
            );
            row[reason] = count;
            row.totalClosed += count;
          });
          return row;
        })
        .sort((a, b) => a.key.localeCompare(b.key));

      setCloseAnalysisLeadCache(Object.fromEntries(Array.from(leadMap.entries())));
      setCloseAnalysisPrimaryDetail(primaryDetail);
      setCloseAnalysisStageDetail(stageDetail);
      setCloseAnalysisBudgetDetail(budgetDetail);
      setCloseAnalysisCompetitorDetail(competitorDetail);
      setCloseAnalysisSubDetail(subDetail);
      setCloseAnalysisDailyDetail(dailyDetail);
      setCloseAnalysisDailyRows(dailyRows);
      setCloseAnalysisSummary({
        totalClosed: includedLeadIds.size,
        completed,
        lost,
      });
      setCloseAnalysisLoading(false);
    } catch (err: any) {
      console.error('close analysis load failed', err);
      setCloseAnalysisError(err?.message || 'Failed to load close analysis.');
      setCloseAnalysisLoading(false);
    }
  }, [db, isSalesManager, isTopAdmin, leadStatuses, toDateMaybe, ymdKey]);
  const [leadCache, setLeadCache] = useState<Record<string, any>>({});
  const [leadCacheLoading, setLeadCacheLoading] = useState(false);

  const startOfToday = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const startOfTomorrow = useMemo(() => {
    const d = new Date(startOfToday);
    d.setDate(d.getDate() + 1);
    return d;
  }, [startOfToday]);
  const startOfYesterday = useMemo(() => {
    const d = new Date(startOfToday);
    d.setDate(d.getDate() - 1);
    return d;
  }, [startOfToday]);
  const startOf7dAgo = useMemo(() => {
    const d = new Date(startOfToday);
    d.setDate(d.getDate() - 7);
    return d;
  }, [startOfToday]);

  const journeyPeriodBounds = useMemo(() => {
    const start = new Date(startOfToday);
    if (journeyRange === '7d') {
      start.setDate(start.getDate() - 7);
    } else if (journeyRange === '30d') {
      start.setDate(start.getDate() - 30);
    } else if (journeyRange === 'month') {
      start.setDate(1);
    }
    const end = new Date(startOfTomorrow);
    return { start, end };
  }, [journeyRange, startOfToday, startOfTomorrow]);

  useEffect(() => {
    if (!isAdminish) return;
    // Used to map activity `byEmail` -> user name.
    const q = query(collection(db, 'users'), orderBy('name', 'asc'));
    return onSnapshot(
      q,
      (snap) => {
        // Store only the fields we need for dashboard labels so unrelated user doc updates
        // (or repeated snapshots) don't retrigger heavy dashboard KPI calculations.
        const next = snap.docs.map((d) => {
          const v = d.data() as any;
          return {
            id: d.id,
            email: typeof v?.email === 'string' ? v.email : null,
            name: typeof v?.name === 'string' ? v.name : null,
            roleId: typeof v?.roleId === 'string' ? v.roleId : null,
            role: typeof v?.role === 'string' ? v.role : null,
          };
        });
        setUserDirectory((prev) => {
          if (!Array.isArray(prev) || prev.length !== next.length) return next as any;
          for (let i = 0; i < next.length; i += 1) {
            const a: any = prev[i];
            const b: any = next[i];
            if (!a || !b) return next as any;
            if (String(a.id) !== String(b.id)) return next as any;
            if (String(a.email || '') !== String(b.email || '')) return next as any;
            if (String(a.name || '') !== String(b.name || '')) return next as any;
            if (String(a.roleId || '') !== String(b.roleId || '')) return next as any;
            if (String(a.role || '') !== String(b.role || '')) return next as any;
          }
          return prev;
        });
      },
      () => setUserDirectory([])
    );
  }, [db, isAdminish]);

  useEffect(() => {
    // Status docs: used only for UI labels (so Dashboard shows "New" not "new").
    const q = query(collection(db, 'leadStatuses'), orderBy('order', 'asc'));
    return onSnapshot(
      q,
      (snap) => {
        // Store only the fields needed for label/order lookup so repetitive snapshots don't
        // cause the dashboard to continuously refetch KPIs.
        const next = snap.docs.map((d) => {
          const v = d.data() as any;
          return {
            id: d.id,
            order: typeof v?.order === 'number' ? v.order : null,
            label: typeof v?.label === 'string' ? v.label : null,
            name: typeof v?.name === 'string' ? v.name : null,
            color: typeof v?.color === 'string' ? v.color : null,
          };
        });
        setLeadStatuses((prev) => {
          if (!Array.isArray(prev) || prev.length !== next.length) return next as any;
          for (let i = 0; i < next.length; i += 1) {
            const a: any = prev[i];
            const b: any = next[i];
            if (!a || !b) return next as any;
            if (String(a.id) !== String(b.id)) return next as any;
            if (Number(a.order ?? -1) !== Number(b.order ?? -1)) return next as any;
            if (String(a.label || '') !== String(b.label || '')) return next as any;
            if (String(a.name || '') !== String(b.name || '')) return next as any;
            if (String(a.color || '') !== String(b.color || '')) return next as any;
          }
          return prev;
        });
      },
      () => setLeadStatuses([])
    );
  }, [db]);

  useEffect(() => {
    if ((!isTopAdmin && !isSalesManager) || dashboardSection !== 'close-analysis') return;
    loadCloseAnalysis(closeAnalysisMonthKey);
  }, [closeAnalysisMonthKey, dashboardSection, isSalesManager, isTopAdmin, loadCloseAnalysis]);

  const statusLabelMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of leadStatuses) {
      const label = String((s as any)?.label || (s as any)?.name || s.id || '').trim();
      if (!label) continue;
      m.set(String(s.id).toLowerCase(), label);
      m.set(label.toLowerCase(), label);
    }
    return m;
  }, [leadStatuses]);

  const journeyStatusOptions = useMemo(
    () =>
      leadStatuses
        .map((s: any, idx) => {
          const label = String(s?.label || s?.name || s?.id || '').trim();
          const id = String(s?.id || label).trim();
          return {
            id,
            key: id.toLowerCase(),
            label,
            order: typeof s?.order === 'number' ? s.order : idx + 1,
            color: typeof s?.color === 'string' && /^#([0-9a-fA-F]{6})$/.test(s.color) ? s.color : '',
          };
        })
        .filter((s) => s.id && s.label)
        .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label)),
    [leadStatuses]
  );

  const journeyStatusKeyFor = useCallback(
    (value: any) => {
      const raw = String(value || '').trim();
      if (!raw) return '';
      const norm = raw.toLowerCase();
      const match = journeyStatusOptions.find(
        (s) => s.key === norm || s.label.toLowerCase() === norm
      );
      return match?.key || '';
    },
    [journeyStatusOptions]
  );

  const journeyStatusLabelForKey = useCallback(
    (key: string) => {
      const norm = String(key || '').trim().toLowerCase();
      if (!norm) return '—';
      return journeyStatusOptions.find((s) => s.key === norm)?.label || statusLabelMap.get(norm) || key;
    },
    [journeyStatusOptions, statusLabelMap]
  );

  const emailToName = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of userDirectory) {
      const email = String((u as any)?.email || '').trim().toLowerCase();
      const name = String((u as any)?.name || '').trim();
      if (email && name) m.set(email, name);
    }
    return m;
  }, [userDirectory]);

  const nameToEmail = useMemo(() => {
    const m = new Map<string, string>();
    const keyForName = (v: any) =>
      String(v || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');
    for (const u of userDirectory) {
      const email = String((u as any)?.email || '').trim().toLowerCase();
      const nameKey = keyForName((u as any)?.name);
      if (email && nameKey) m.set(nameKey, email);
    }
    return m;
  }, [userDirectory]);
  const effectiveAssigneeEmail = useCallback(
    (lead: any) => {
      const raw = String(lead?.assignedTo || '').trim().toLowerCase();
      return raw;
    },
    []
  );
  const resolveAssigneeLabel = useCallback(
    (lead: any) => {
      const explicit = String(lead?.assignedToName || '').trim();
      if (explicit) return explicit;
      const email = effectiveAssigneeEmail(lead);
      if (!email) return 'Unassigned';
      if (email.includes('@')) {
        return (
          emailToName.get(email) ||
          email.split('@')[0] ||
          email
        );
      }
      return email;
    },
    [effectiveAssigneeEmail, emailToName]
  );

  const statusIdForLabel = useCallback(
    (labelOrId: string) => {
      const norm = String(labelOrId || '').trim().toLowerCase();
      if (!norm) return '';
      const byId = leadStatuses.find((s) => String(s.id || '').trim().toLowerCase() === norm);
      if (byId?.id) return byId.id;
      const byLabel = leadStatuses.find((s) => {
        const lbl = String((s as any)?.label || (s as any)?.name || '').trim().toLowerCase();
        return lbl === norm;
      });
      return byLabel?.id || '';
    },
    [leadStatuses]
  );

  const isClosedLike = useCallback(
    (status: any) => {
      const norm = (v: any) => String(v || '').trim().toLowerCase();
      const n = norm(status);
      if (!n) return false;
      if (n === 'closed' || n === 'completed') return true;
      const mapped = statusLabelMap.get(n);
      const m = norm(mapped);
      return m === 'closed' || m === 'completed';
    },
    [statusLabelMap]
  );

  useEffect(() => {
    if (dashboardSection !== 'sales-journey' || !canViewSalesJourney) {
      setJourneyTree(null);
      setJourneySiteVisitedCount(0);
      setJourneyLeadCache({});
      setJourneySourceOptions([]);
      setJourneyExpanded({});
      setJourneyOpenBranch(null);
      return;
    }

    let cancelled = false;
    const run = async () => {
      setJourneyLoading(true);
      setJourneyError(null);
      setJourneyTree(null);
      setJourneySiteVisitedCount(0);
      setJourneyOpenBranch(null);
      try {
        const qActs = query(
          collectionGroup(db, 'activities'),
          where('at', '>=', Timestamp.fromDate(journeyPeriodBounds.start)),
          where('at', '<', Timestamp.fromDate(journeyPeriodBounds.end)),
          orderBy('at', 'asc'),
          limit(5000)
        );
        const actSnap = await getDocs(qActs);
        if (cancelled) return;

        const actsByLead = new Map<string, Array<any>>();
        const visitActsByLead = new Map<string, Array<any>>();
        const assignActsByLead = new Map<string, Array<any>>();
        actSnap.docs.forEach((d) => {
          const leadId = d.ref?.parent?.parent?.id || '';
          if (!leadId) return;
          const a = d.data() as any;
          const at = toDateMaybe(a.at);
          if (!at) return;
          const type = String(a?.type || '').trim().toLowerCase();
          const event = String(a?.event || '').trim().toLowerCase();
          if (a?.statusTo) {
            const statusKey = journeyStatusKeyFor(a.statusTo);
            if (statusKey) {
              const row = { id: d.id, leadId, at, statusKey, raw: a };
              const list = actsByLead.get(leadId) || [];
              list.push(row);
              actsByLead.set(leadId, list);
              const statusLabel = journeyStatusLabelForKey(statusKey).trim().toLowerCase();
              if (statusLabel === 'site visited' || statusKey === 'site-visited') {
                const visitList = visitActsByLead.get(leadId) || [];
                visitList.push({ id: d.id, leadId, at, raw: a });
                visitActsByLead.set(leadId, visitList);
              }
            }
          }
          if (type === 'visit' || type.startsWith('visit')) {
            const list = visitActsByLead.get(leadId) || [];
            list.push({ id: d.id, leadId, at, raw: a });
            visitActsByLead.set(leadId, list);
          }
          if (event === 'assign' || a?.assignedTo || a?.assignedFrom) {
            const list = assignActsByLead.get(leadId) || [];
            list.push({ id: d.id, leadId, at, raw: a });
            assignActsByLead.set(leadId, list);
          }
        });

        const leadIds = Array.from(new Set([...Array.from(actsByLead.keys()), ...Array.from(visitActsByLead.keys())]));
        const leadMap = new Map<string, any>();
        const leadIdBatches: string[][] = [];
        for (let i = 0; i < leadIds.length; i += 10) {
          leadIdBatches.push(leadIds.slice(i, i + 10));
        }
        for (let i = 0; i < leadIdBatches.length; i += 8) {
          const snaps = await Promise.all(
            leadIdBatches.slice(i, i + 8).map((batchIds) =>
              getDocs(query(collection(db, 'leads'), where(documentId(), 'in', batchIds))).catch(() => null)
            )
          );
          snaps.forEach((leadSnap) => {
            leadSnap?.forEach((d) => leadMap.set(d.id, { id: d.id, ...(d.data() as any) }));
          });
          if (cancelled) return;
        }
        if (cancelled) return;

        const selectedAssignee = String(journeySalesperson || 'all').trim().toLowerCase();
        const selectedSource = String(journeySource || 'all').trim().toLowerCase();
        const sourceSet = new Set<string>();
        const closedKey =
          journeyStatusOptions.find((s) => s.label.toLowerCase() === 'closed')?.key ||
          journeyStatusOptions.find((s) => s.key === 'closed')?.key ||
          '';
        const reopenKey =
          journeyStatusOptions.find((s) => s.label.toLowerCase() === 'reopen')?.key ||
          journeyStatusOptions.find((s) => s.key === 'reopen')?.key ||
          '';
        const completedKey = '__completed__';
        const isCompletedClose = (lead: any) => {
          const fields = [
            lead?.closeReason,
            lead?.closePrimaryReason,
            lead?.closedAnalysis?.primaryReason,
            lead?.closedAnalysis?.primaryReasonBase,
          ];
          return fields.some((v) => String(v || '').trim().toLowerCase() === 'completed');
        };
        const emptyNode = (key: string, label: string) => ({
          key,
          label,
          leadIds: [] as string[],
          children: {} as Record<string, any>,
        });
        const rootLabel =
          selectedAssignee === 'all'
            ? 'Assigned leads with movement'
            : `Assigned to ${emailToName.get(selectedAssignee) || selectedAssignee}`;
        const root = emptyNode('assigned', rootLabel);
        const siteVisitedLeadIds = new Set<string>();

        const simplify = (rawSeq: string[], lead: any) => {
          const out: string[] = [];
          const seen = new Set<string>();
          rawSeq.forEach((key) => {
            if (!key) return;
            const prev = out[out.length - 1] || '';
            if (prev === key) return;
            const keepClosedReopen = closedKey && reopenKey && prev === closedKey && key === reopenKey;
            if (!keepClosedReopen && seen.has(key)) return;
            out.push(key);
            seen.add(key);
          });
          if (
            closedKey &&
            out.includes(closedKey) &&
            out[out.length - 1] === closedKey &&
            isCompletedClose(lead)
          ) {
            out.push(completedKey);
          }
          return out;
        };

        const sourceMatches = (lead: any) => {
          const sourceLabel = String(lead?.source || lead?.leadSource || 'Unknown').trim() || 'Unknown';
          sourceSet.add(sourceLabel);
          return selectedSource === 'all' || sourceLabel.toLowerCase() === selectedSource;
        };
        const assigneeMatches = (lead: any) => {
          const assignee = String(lead?.assignedTo || '').trim().toLowerCase();
          if (!assignee) return false;
          return selectedAssignee === 'all' || assignee === selectedAssignee;
        };
        const roleByEmail = new Map<string, string>();
        for (const user of userDirectory) {
          const email = String((user as any)?.email || '').trim().toLowerCase();
          const role = String((user as any)?.roleId || (user as any)?.role || '').trim().toLowerCase();
          if (email) roleByEmail.set(email, role);
        }
        const activityActorEmail = (activity: any) =>
          String(activity?.byEmail || activity?.createdByEmail || activity?.createdBy || '').trim().toLowerCase();
        const isSalesTeamEmail = (email: string) => {
          const role = roleByEmail.get(String(email || '').trim().toLowerCase()) || '';
          return role === 'sales-team' || role === 'salesteam';
        };
        const isManagerOrAdminEmail = (email: string) => {
          const role = roleByEmail.get(String(email || '').trim().toLowerCase()) || '';
          return role === 'sales-manager' || role === 'salesmanager' || role === 'admin' || role === 'super-admin';
        };
        const fullAssignActsByLead = new Map<string, Array<any>>(assignActsByLead);
        const salesTeamTouchActsByLead = new Map<string, Array<any>>();
        const visitLeadIdsForAttribution = Array.from(visitActsByLead.keys()).filter((leadId) => {
          const lead = leadMap.get(leadId);
          return lead && sourceMatches(lead);
        });
        for (let i = 0; i < visitLeadIdsForAttribution.length; i += 20) {
          const batchIds = visitLeadIdsForAttribution.slice(i, i + 20);
          await Promise.all(
            batchIds.map(async (leadId) => {
              const snap = await getDocs(collection(db, 'leads', leadId, 'activities')).catch(() => null);
              if (!snap) return;
              const assignRows: any[] = [];
              const salesTeamRows: any[] = [];
              snap.forEach((d) => {
                const a = d.data() as any;
                const at = toDateMaybe(a?.at);
                if (!at) return;
                const event = String(a?.event || '').trim().toLowerCase();
                const row = { id: d.id, leadId, at, raw: a };
                if (event === 'assign' || a?.assignedTo || a?.assignedFrom) assignRows.push(row);
                if (isSalesTeamEmail(activityActorEmail(a))) salesTeamRows.push(row);
              });
              assignRows.sort((a, b) => a.at.getTime() - b.at.getTime());
              salesTeamRows.sort((a, b) => a.at.getTime() - b.at.getTime());
              fullAssignActsByLead.set(leadId, assignRows);
              salesTeamTouchActsByLead.set(leadId, salesTeamRows);
            })
          );
          if (cancelled) return;
        }
        const assigneeAtVisit = (leadId: string, visitAt: Date, lead: any) => {
          const rows = (fullAssignActsByLead.get(leadId) || [])
            .filter((a: any) => a.at.getTime() <= visitAt.getTime() && String(a?.raw?.assignedTo || '').trim())
            .sort((a: any, b: any) => b.at.getTime() - a.at.getTime());
          const historical = String(rows[0]?.raw?.assignedTo || '').trim().toLowerCase();
          if (historical && isSalesTeamEmail(historical)) return historical;
          const salesTeamRows = (salesTeamTouchActsByLead.get(leadId) || [])
            .filter((a: any) => a.at.getTime() <= visitAt.getTime())
            .sort((a: any, b: any) => b.at.getTime() - a.at.getTime());
          const lastSalesTeamActor = activityActorEmail(salesTeamRows[0]?.raw || {});
          if ((!historical || isManagerOrAdminEmail(historical)) && lastSalesTeamActor) return lastSalesTeamActor;
          if (historical) return historical;
          return String(lead?.assignedTo || '').trim().toLowerCase();
        };

        for (const leadId of visitLeadIdsForAttribution) {
          const lead = leadMap.get(leadId);
          if (!lead) continue;
          const visits = visitActsByLead.get(leadId) || [];
          const hasVisitForSelectedOwner = visits.some((v: any) => {
            const owner = assigneeAtVisit(leadId, v.at, lead);
            if (!owner) return false;
            return selectedAssignee === 'all' || owner === selectedAssignee;
          });
          if (hasVisitForSelectedOwner) siteVisitedLeadIds.add(leadId);
        }

        const addPath = (leadId: string, seq: string[]) => {
          root.leadIds.push(leadId);
          let node = root;
          seq.forEach((key) => {
            const label = key === completedKey ? 'Completed' : journeyStatusLabelForKey(key);
            if (!node.children[key]) node.children[key] = emptyNode(key, label);
            node.children[key].leadIds.push(leadId);
            node = node.children[key];
          });
        };

        for (const leadId of leadIds) {
          const lead = leadMap.get(leadId);
          if (!lead) continue;
          if (!assigneeMatches(lead)) continue;
          if (!sourceMatches(lead)) continue;
          const rawSeq = (actsByLead.get(leadId) || [])
            .slice()
            .sort((a, b) => a.at.getTime() - b.at.getTime())
            .map((a) => a.statusKey);
          const seq = simplify(rawSeq, lead);
          if (!seq.length) continue;
          addPath(leadId, seq);
        }

        const sortNode = (node: any) => {
          const ordered: Record<string, any> = {};
          Object.values(node.children || {})
            .sort((a: any, b: any) => {
              const ao = a.key === completedKey ? 9999 : (journeyStatusOptions.find((s) => s.key === a.key)?.order ?? 9998);
              const bo = b.key === completedKey ? 9999 : (journeyStatusOptions.find((s) => s.key === b.key)?.order ?? 9998);
              return ao - bo || String(a.label).localeCompare(String(b.label));
            })
            .forEach((child: any) => {
              sortNode(child);
              ordered[child.key] = child;
            });
          node.children = ordered;
        };
        sortNode(root);

        setJourneyLeadCache(Object.fromEntries(Array.from(leadMap.entries())));
        setJourneySourceOptions(Array.from(sourceSet).sort((a, b) => a.localeCompare(b)));
        setJourneySiteVisitedCount(siteVisitedLeadIds.size);
        setJourneyTree(root);
      } catch (err: any) {
        console.error('sales journey flow failed', err);
        setJourneyError(err?.message || 'Failed to load Sales Journey Flow.');
        setJourneyTree(null);
      } finally {
        if (!cancelled) setJourneyLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [
    canViewSalesJourney,
    dashboardSection,
    db,
    emailToName,
    journeyPeriodBounds,
    journeySalesperson,
    journeySource,
    journeyStatusKeyFor,
    journeyStatusLabelForKey,
    journeyStatusOptions,
    toDateMaybe,
    userDirectory,
  ]);

  const funnelPct = (num: number | null, den: number | null) => {
    if (!den || den <= 0) return null;
    if (num == null) return null;
    return Math.round((num / den) * 100);
  };

  const openLead = useCallback((lead: any) => {
    setDetailLeadId(lead?.id || null);
    setDetailLeadFallback(lead || null);
    setDetailDrawerOpen(Boolean(lead?.id));
  }, []);

  const closeLead = useCallback(() => {
    setDetailDrawerOpen(false);
  }, []);
  const handleLeadDrawerExited = useCallback(() => {
    setDetailLeadId(null);
    setDetailLeadFallback(null);
  }, []);
  const openUnassignedFromKpi = useCallback(() => {
    setDashboardSection('my-work');
    setUnassignedOpen(true);
  }, []);

  useEffect(() => {
    const needsLeadLists = dashboardSection === 'my-work' || dashboardSection === 'new-leads' || dashboardSection === 'team';
    if (!needsLeadLists) {
      setTodayDue([]);
      setOverdue([]);
      setScheduledToday([]);
      setNewToday([]);
      setUnassignedLeads([]);
      return;
    }

    setError(null);

    // Lists (realtime, small limits)
    const unsub: Array<() => void> = [];
    try {
      let dueRows: any[] = [];
      let noFollowUpRows: any[] = [];
      const recomputeTodayDue = () => {
        const merged = new Map<string, any>();
        dueRows.forEach((r) => merged.set(String(r.id), r));
        noFollowUpRows
          .filter((r) => !isClosedLike(r.status))
          .forEach((r) =>
            merged.set(String(r.id), {
              ...r,
              // Treat open leads with no follow-up as "today follow-up" for dashboard.
              nextFollowUpDate: Timestamp.fromDate(startOfToday),
              __autoTodayFollowUp: true,
            })
          );
        setTodayDue(
          Array.from(merged.values())
            .sort((a, b) => {
              const da = a?.nextFollowUpDate?.toDate ? a.nextFollowUpDate.toDate().getTime() : 0;
              const db = b?.nextFollowUpDate?.toDate ? b.nextFollowUpDate.toDate().getTime() : 0;
              return da - db;
            })
            .slice(0, 100)
        );
      };

      const qDue = query(
        collection(db, 'leads'),
        where('nextFollowUpDate', '>=', Timestamp.fromDate(startOfToday)),
        where('nextFollowUpDate', '<', Timestamp.fromDate(startOfTomorrow)),
        orderBy('nextFollowUpDate', 'asc'),
        limit(100)
      );
      unsub.push(
        onSnapshot(
          qDue,
          (snap) => {
            dueRows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
            recomputeTodayDue();
          },
          (err) => setError(err?.message || 'Failed to load today follow-ups.')
        )
      );

      const qNoFollowUp = query(
        collection(db, 'leads'),
        where('nextFollowUpDate', '==', null),
        limit(100)
      );
      unsub.push(
        onSnapshot(
          qNoFollowUp,
          (snap) => {
            noFollowUpRows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
            recomputeTodayDue();
          },
          () => {
            noFollowUpRows = [];
            recomputeTodayDue();
          }
        )
      );

      const qOver = query(
        collection(db, 'leads'),
        where('nextFollowUpDate', '<', Timestamp.fromDate(startOfToday)),
        orderBy('nextFollowUpDate', 'desc'),
        limit(100)
      );
      unsub.push(
        onSnapshot(
          qOver,
          (snap) => setOverdue(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))),
          (err) => setError(err?.message || 'Failed to load overdue follow-ups.')
        )
      );

      const qAppt = query(
        collection(db, 'leads'),
        where('appointment.scheduledAt', '>=', Timestamp.fromDate(startOfToday)),
        where('appointment.scheduledAt', '<', Timestamp.fromDate(startOfTomorrow)),
        orderBy('appointment.scheduledAt', 'asc'),
        limit(25)
      );
      unsub.push(
        onSnapshot(
          qAppt,
          (snap) => {
            const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
            // Some docs might contain cancelled/completed; keep scheduled only.
            setScheduledToday(rows.filter((r) => (r.appointment?.status || 'scheduled') === 'scheduled'));
          },
          () => {
            // Avoid noisy dashboard errors if this index isn't available yet.
            setScheduledToday([]);
          }
        )
      );

      const qNew = query(
        collection(db, 'leads'),
        where('createdAt', '>=', Timestamp.fromDate(startOfToday)),
        orderBy('createdAt', 'desc'),
        limit(10)
      );
      unsub.push(
        onSnapshot(
          qNew,
          (snap) => setNewToday(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))),
          () => setNewToday([])
        )
      );

      const qUnassigned = query(
        collection(db, 'leads'),
        where('assignedTo', '==', null),
        limit(100)
      );
      unsub.push(
        onSnapshot(
          qUnassigned,
          (snap) => setUnassignedLeads(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))),
          () => setUnassignedLeads([])
        )
      );
    } catch (err: any) {
      setError(err?.message || 'Failed to load dashboard lists.');
    }

    return () => {
      unsub.forEach((fn) => fn());
    };
  }, [dashboardSection, db, isClosedLike, startOfToday, startOfTomorrow]);

  useEffect(() => {
    if (!dashboardSection) {
      setCountNewToday(null);
      setCountDueToday(null);
      setCountOverdue(null);
      setCountApptToday(null);
      setCountClosed7d(null);
      setAvgDaysToCloseCompleted7d(null);
      setCountUnassigned(null);
      setFunnelOverall({ newCount: null, connectedCount: null, visitCount: null, closedCount: null, completedCount: null });
      setFunnelBySource([]);
      setFunnelByProject([]);
      setFunnelByAgent([]);
      setPipelineSnapshot([]);
      setPipelineClosedReasonCounts({});
      return;
    }
    let cancelled = false;
    const run = async () => {
      try {
        setKpiError(null);
        setAvgDaysToCloseCompleted7d(null);
        setPipelineSnapshot([]);
        setFunnelOverall({ newCount: null, connectedCount: null, visitCount: null, closedCount: null, completedCount: null });
        setFunnelBySource([]);
        setFunnelByProject([]);
        setFunnelByAgent([]);
        const qNew = query(collection(db, 'leads'), where('createdAt', '>=', Timestamp.fromDate(startOfToday)));
        const qDue = query(
          collection(db, 'leads'),
          where('nextFollowUpDate', '>=', Timestamp.fromDate(startOfToday)),
          where('nextFollowUpDate', '<', Timestamp.fromDate(startOfTomorrow))
        );
        const qNoFollowUp = query(collection(db, 'leads'), where('nextFollowUpDate', '==', null));
        const qOver = query(collection(db, 'leads'), where('nextFollowUpDate', '<', Timestamp.fromDate(startOfToday)));
        const qAppt = query(
          collection(db, 'leads'),
          where('appointment.scheduledAt', '>=', Timestamp.fromDate(startOfToday)),
          where('appointment.scheduledAt', '<', Timestamp.fromDate(startOfTomorrow))
        );
        const closed7dFromActivities = async () => {
          const start = Timestamp.fromDate(startOf7dAgo);
          const end = Timestamp.fromDate(startOfTomorrow);
          const q = query(
            collectionGroup(db, 'activities'),
            where('at', '>=', start),
            where('at', '<', end),
            orderBy('at', 'desc'),
            limit(5000)
          );
          const snap = await getDocs(q);
          const closedLeadIds = new Set<string>();
          const norm = (v: any) => String(v || '').trim().toLowerCase();
          const isClosed = (v: any) => {
            const n = norm(v);
            if (!n) return false;
            if (n === 'closed' || n === 'completed') return true;
            const mapped = statusLabelMap.get(n);
            const m = norm(mapped);
            return m === 'closed' || m === 'completed';
          };
          for (const d of snap.docs) {
            const a = d.data() as any;
            if (!isClosed(a.statusTo)) continue;
            const leadId = d.ref?.parent?.parent?.id || '';
            if (leadId) closedLeadIds.add(leadId);
          }
          return closedLeadIds.size;
        };
        const avgDaysToCloseCompleted7dFromLeads = async () => {
          const start = Timestamp.fromDate(startOf7dAgo);
          const end = Timestamp.fromDate(startOfTomorrow);
          const q = query(
            collection(db, 'leads'),
            where('closeReason', '==', 'Completed'),
            where('closedAt', '>=', start),
            where('closedAt', '<', end),
            orderBy('closedAt', 'desc'),
            limit(5000)
          );
          const snap = await getDocs(q);
          const norm = (v: any) => String(v || '').trim().toLowerCase();
          const isClosedStatus = (v: any) => {
            const n = norm(v);
            if (!n) return false;
            if (n === 'closed' || n === 'completed') return true;
            const mapped = statusLabelMap.get(n);
            const m = norm(mapped);
            return m === 'closed' || m === 'completed';
          };
          const toDate = (ts: any): Date | null => (ts?.toDate ? ts.toDate() : null);
          let sumDays = 0;
          let n = 0;
          snap.docs.forEach((docSnap) => {
            const lead = docSnap.data() as any;
            if (!isClosedStatus(lead.status)) return;
            const created = toDate(lead.createdAt) || toDate(lead.importedAt);
            const closed = toDate(lead.closedAt);
            if (!created || !closed) return;
            const diffMs = closed.getTime() - created.getTime();
            if (diffMs < 0) return;
            sumDays += diffMs / (1000 * 60 * 60 * 24);
            n += 1;
          });
          if (!n) return 0;
          return Math.round((sumDays / n) * 10) / 10;
        };
        const funnelKpis7d = async () => {
          const start7d = Timestamp.fromDate(startOf7dAgo);
          const endTomorrow = Timestamp.fromDate(startOfTomorrow);

          const leadsSnap = await getDocs(
            query(
              collection(db, 'leads'),
              where('createdAt', '>=', start7d),
              limit(5000)
            )
          );
          const leads = leadsSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
          const leadById = new Map<string, any>();
          leads.forEach((l) => leadById.set(String(l.id), l));

          const flags = new Map<string, { connected: boolean; visit: boolean }>();
          const ensure = (id: string) => {
            const ex = flags.get(id);
            if (ex) return ex;
            const created = { connected: false, visit: false };
            flags.set(id, created);
            return created;
          };

          const actsSnap = await getDocs(
            query(
              collectionGroup(db, 'activities'),
              where('at', '>=', start7d),
              where('at', '<', endTomorrow),
              orderBy('at', 'desc'),
              limit(5000)
            )
          );
          for (const d of actsSnap.docs) {
            const a = d.data() as any;
            const leadId = d.ref?.parent?.parent?.id || '';
            if (!leadId) continue;
            if (!leadById.has(leadId)) continue;
            const f = ensure(leadId);
            if (a.type === 'call' && String(a.callOutcome || '').trim().toLowerCase() === 'connected') {
              f.connected = true;
            }
            if (a.type === 'visit') {
              f.visit = true;
            }
          }

          const overall = {
            newCount: leads.length,
            connectedCount: 0,
            visitCount: 0,
            closedCount: 0,
            completedCount: 0,
          };

          type Row = {
            key: string;
            label: string;
            newCount: number;
            connectedCount: number;
            visitCount: number;
            closedCount: number;
            completedCount: number;
          };

          const bump = (m: Map<string, Row>, key: string, label: string, inc: Partial<Row>) => {
            const ex = m.get(key) || {
              key,
              label,
              newCount: 0,
              connectedCount: 0,
              visitCount: 0,
              closedCount: 0,
              completedCount: 0,
            };
            ex.newCount += inc.newCount || 0;
            ex.connectedCount += inc.connectedCount || 0;
            ex.visitCount += inc.visitCount || 0;
            ex.closedCount += inc.closedCount || 0;
            ex.completedCount += inc.completedCount || 0;
            m.set(key, ex);
          };

          const bySource = new Map<string, Row>();
          const byProject = new Map<string, Row>();
          const byAgent = new Map<string, Row>();

          const normSrc = (v: any) => String(v || '').trim().toLowerCase().replace(/\s+/g, ' ');
          const srcLabel = (v: any) => {
            const n = normSrc(v);
            if (!n) return 'Unknown';
            if (n === 'walk in' || n === 'walk-in') return 'Walk In';
            if (n === 'with broker' || n === 'with brocker' || n === 'broker' || n === 'brocker') return 'With Broker';
            if (n === 'social ad' || n === 'social ads' || n === 'social lead' || n === 'meta lead') return 'Social Ad';
            return String(v || 'Unknown').trim();
          };
          const projectLabel = (l: any) => String(l?.projectName || '').trim() || 'No Project';
          const agentLabel = (l: any) => resolveAssigneeLabel(l);

          for (const l of leads) {
            const id = String(l.id);
            const f = flags.get(id) || { connected: false, visit: false };
            const connected = f.connected;
            const visit = f.visit;
            const closed = isClosedLike(l.status);
            const completed = closed && String(l.closeReason || '').trim().toLowerCase() === 'completed';

            if (connected) overall.connectedCount += 1;
            if (visit) overall.visitCount += 1;
            if (closed) overall.closedCount += 1;
            if (completed) overall.completedCount += 1;

            const sLabel = srcLabel(l.source);
            bump(bySource, normSrc(sLabel) || sLabel, sLabel, {
              newCount: 1,
              connectedCount: connected ? 1 : 0,
              visitCount: visit ? 1 : 0,
              closedCount: closed ? 1 : 0,
              completedCount: completed ? 1 : 0,
            });

            const pLabel = projectLabel(l);
            bump(byProject, String(l.projectId || '').trim() || pLabel, pLabel, {
              newCount: 1,
              connectedCount: connected ? 1 : 0,
              visitCount: visit ? 1 : 0,
              closedCount: closed ? 1 : 0,
              completedCount: completed ? 1 : 0,
            });

            const aLabel = agentLabel(l);
            bump(byAgent, effectiveAssigneeEmail(l) || aLabel, aLabel, {
              newCount: 1,
              connectedCount: connected ? 1 : 0,
              visitCount: visit ? 1 : 0,
              closedCount: closed ? 1 : 0,
              completedCount: completed ? 1 : 0,
            });
          }

          const sortRows = (m: Map<string, Row>) =>
            Array.from(m.values()).sort((a, b) => b.newCount - a.newCount || a.label.localeCompare(b.label));

          return {
            overall,
            bySource: sortRows(bySource),
            byProject: sortRows(byProject),
            byAgent: sortRows(byAgent),
          };
        };
        const pipelineSnapshotFromCounts = async () => {
          const leadRef = collection(db, 'leads');
          const start7d = Timestamp.fromDate(startOf7dAgo);
          const endTomorrow = Timestamp.fromDate(startOfTomorrow);

          const ids = {
            new: statusIdForLabel('New'),
            qualify: statusIdForLabel('Qualify'),
            appointment: statusIdForLabel('Appointment'),
            siteVisited: statusIdForLabel('Site Visited') || statusIdForLabel('site-visited'),
            negotiation: statusIdForLabel('Negotiation'),
            closed: statusIdForLabel('Closed'),
            cnr: statusIdForLabel('CNR'),
            prospect: statusIdForLabel('Prospect'),
            reopen: statusIdForLabel('Reopen'),
          };

          const contactedIds = [ids.qualify, ids.appointment, ids.prospect, ids.reopen].filter(Boolean);

          // Current counts
          const qStageNew = ids.new ? query(leadRef, where('status', '==', ids.new)) : null;
          const qStageContacted =
            contactedIds.length > 0 ? query(leadRef, where('status', 'in', contactedIds.slice(0, 10))) : null;
          const qStageVisit = ids.siteVisited ? query(leadRef, where('status', '==', ids.siteVisited)) : null;
          const qStageNeg = ids.negotiation ? query(leadRef, where('status', '==', ids.negotiation)) : null;
          const qStageClosed = ids.closed ? query(leadRef, where('status', '==', ids.closed)) : null;

          const safeCount = async (qAny: any) => {
            if (!qAny) return 0;
            try {
              const r = await getCountFromServer(qAny);
              return r.data().count || 0;
            } catch {
              return null;
            }
          };

          const [cNewAll, cContactedAll, cVisitAll, cNegAll, cClosedAll] = await Promise.all([
            safeCount(qStageNew),
            safeCount(qStageContacted),
            safeCount(qStageVisit),
            safeCount(qStageNeg),
            safeCount(qStageClosed),
          ]);

          // Trend (7 days): "Entered" counts
          const qNew7d = query(leadRef, where('createdAt', '>=', start7d));

          const enteredFromActivities = async () => {
            // Count unique leads that entered each stage via status changes in the last 7 days.
            const qActs = query(
              collectionGroup(db, 'activities'),
              where('at', '>=', start7d),
              where('at', '<', endTomorrow),
              orderBy('at', 'desc'),
              limit(5000)
            );
            const snap = await getDocs(qActs);

            const mapToId = (statusTo: any) => {
              const raw = String(statusTo || '').trim();
              if (!raw) return '';
              // statusTo can be an id or a label; normalize using statusLabelMap (id -> label).
              const mapped = statusLabelMap.get(raw.toLowerCase());
              const lbl = mapped || raw;
              return statusIdForLabel(lbl) || statusIdForLabel(raw) || raw;
            };

            const stageSets: Record<string, Set<string>> = {
              contacted: new Set(),
              visit: new Set(),
              negotiation: new Set(),
            };

            for (const d of snap.docs) {
              const a = d.data() as any;
              const to = mapToId(a.statusTo);
              if (!to) continue;
              const leadId = d.ref?.parent?.parent?.id || '';
              if (!leadId) continue;
              if (contactedIds.includes(to)) stageSets.contacted.add(leadId);
              else if (ids.siteVisited && to === ids.siteVisited) stageSets.visit.add(leadId);
              else if (ids.negotiation && to === ids.negotiation) stageSets.negotiation.add(leadId);
            }
            return {
              contacted: stageSets.contacted.size,
              visit: stageSets.visit.size,
              negotiation: stageSets.negotiation.size,
            };
          };

          // Avoid composite-index requirement by counting closings using `closedAt` range only.
          // `closedAt` is set only when a lead is in Closed status, and cleared on Reopen.
          const qClosed7d = query(
            leadRef,
            where('closedAt', '>=', start7d),
            where('closedAt', '<', endTomorrow),
            orderBy('closedAt', 'desc')
          );

          const [cNew7d, moved, closed7dCount] = await Promise.all([
            safeCount(qNew7d),
            enteredFromActivities().catch(() => ({ contacted: null, visit: null, negotiation: null } as any)),
            safeCount(qClosed7d),
          ]);

          const closeReasons = [
            'Completed',
            'Not Interested',
            'Interested in Another Location',
            'Budget / Financial Issue',
            'Distance Issue',
            'Not Reachable (No Response)',
            'Wrong / Invalid Number',
            'Broker Inquiry Only',
            'Loss due to Competitor',
            'Other',
          ];
          const reasonCounts: Record<string, number | null> = {};
          if (ids.closed) {
            const reasonQueries = await Promise.all(
              closeReasons.map(async (reason) => {
                const q = query(leadRef, where('status', '==', ids.closed), where('closeReason', '==', reason));
                const c = await safeCount(q);
                return [reason, c] as const;
              })
            );
            reasonQueries.forEach(([reason, c]) => {
              reasonCounts[reason] = c;
            });
            // Also show leads closed without a reason selected.
            const noReasonCount = await safeCount(query(leadRef, where('status', '==', ids.closed), where('closeReason', '==', null)));
            reasonCounts['(No Reason)'] = noReasonCount;
          }

          return {
            stages: [
              { key: 'new', label: 'New', count: cNewAll, trend7d: cNew7d, color: 'info' },
              { key: 'contacted', label: 'Contacted', count: cContactedAll, trend7d: moved?.contacted ?? null, color: 'primary' },
              { key: 'visit', label: 'Visit', count: cVisitAll, trend7d: moved?.visit ?? null, color: 'success' },
              { key: 'negotiation', label: 'Negotiation', count: cNegAll, trend7d: moved?.negotiation ?? null, color: 'warning' },
              { key: 'closed', label: 'Closed', count: cClosedAll, trend7d: closed7dCount, color: 'secondary' },
            ],
            closedReasonCounts: reasonCounts,
          };
        };
        const qUnassigned = query(collection(db, 'leads'), where('assignedTo', '==', null));

        const needFunnel = dashboardSection === 'funnel';
        const needPipeline = dashboardSection === 'pipeline';

        const [
          cNew,
          cDue,
          noFollowUpSnap,
          cOver,
          cAppt,
          cClosed7d,
          avgCompleted,
          funnel,
          pipeline,
          cUnassigned,
        ] = await Promise.all([
          getCountFromServer(qNew).then((r) => r.data().count).catch(() => null),
          getCountFromServer(qDue).then((r) => r.data().count).catch(() => null),
          getDocs(qNoFollowUp).catch(() => null),
          getCountFromServer(qOver).then((r) => r.data().count).catch(() => null),
          getCountFromServer(qAppt).then((r) => r.data().count).catch(() => null),
          closed7dFromActivities().catch((err: any) => {
            console.error('Closed (7 days) KPI failed', err);
            if (!cancelled) setKpiError(err?.message || 'Failed to load Closed (7 days).');
            return null;
          }),
          avgDaysToCloseCompleted7dFromLeads().catch((err: any) => {
            console.error('Avg Days to Close KPI failed', err);
            if (!cancelled) setKpiError(err?.message || 'Failed to load Avg Days to Close.');
            return null;
          }),
          (needFunnel ? funnelKpis7d() : Promise.resolve({ overall: null, bySource: [], byProject: [], byAgent: [] })).catch((err: any) => {
            console.error('Funnel KPIs failed', err);
            if (!cancelled) setKpiError(err?.message || 'Failed to load Funnel KPIs.');
            return { overall: null, bySource: [], byProject: [], byAgent: [] };
          }),
          (needPipeline ? pipelineSnapshotFromCounts() : Promise.resolve({ stages: [], closedReasonCounts: {} })).catch((err: any) => {
            console.error('Pipeline Snapshot failed', err);
            if (!cancelled) setKpiError(err?.message || 'Failed to load Pipeline Snapshot.');
            return { stages: [], closedReasonCounts: {} };
          }),
          getCountFromServer(qUnassigned).then((r) => r.data().count).catch(() => null),
        ]);

        if (cancelled) return;
        setCountNewToday(cNew);
        const noFollowUpOpenCount =
          noFollowUpSnap?.docs?.filter((d) => !isClosedLike((d.data() as any)?.status)).length ?? 0;
        setCountDueToday((cDue ?? 0) + noFollowUpOpenCount);
        setCountOverdue(cOver);
        setCountApptToday(cAppt);
        setCountClosed7d(cClosed7d);
        setAvgDaysToCloseCompleted7d(avgCompleted);
        setCountUnassigned(cUnassigned);
        if ((funnel as any)?.overall) {
          setFunnelOverall((funnel as any).overall);
          setFunnelBySource(Array.isArray((funnel as any).bySource) ? (funnel as any).bySource : []);
          setFunnelByProject(Array.isArray((funnel as any).byProject) ? (funnel as any).byProject : []);
          setFunnelByAgent(Array.isArray((funnel as any).byAgent) ? (funnel as any).byAgent : []);
        }
        setPipelineSnapshot(Array.isArray((pipeline as any)?.stages) ? (pipeline as any).stages : []);
        setPipelineClosedReasonCounts((pipeline as any)?.closedReasonCounts || {});
      } catch {
        // keep dashboard resilient
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [
    dashboardSection,
    db,
    effectiveAssigneeEmail,
    isClosedLike,
    resolveAssigneeLabel,
    startOf7dAgo,
    startOfToday,
    startOfTomorrow,
    statusIdForLabel,
    statusLabelMap,
  ]);

  useEffect(() => {
    const needsSection = dashboardSection === 'source' || dashboardSection === 'agent' || dashboardSection === 'risk';
    if (!needsSection) {
      setSourcePerfRows([]);
      setTopBrokers([]);
      setAgentPerfRows([]);
      setRiskHighOverdue([]);
      setRiskNoActivityBuckets({ d3: null, d7: null, d14: null });
      setRiskNoActivityLeads([]);
      setRiskHotNoFollowUpLeads([]);
      return;
    }
    let cancelled = false;
    const run = async () => {
      try {
        setSourcePerfRows([]);
        setTopBrokers([]);
        setAgentPerfRows([]);
        setRiskHighOverdue([]);
        setRiskNoActivityBuckets({ d3: null, d7: null, d14: null });
        setRiskNoActivityLeads([]);
        setRiskHotNoFollowUpLeads([]);

        const start7d = Timestamp.fromDate(startOf7dAgo);
        const endTomorrow = Timestamp.fromDate(startOfTomorrow);

        const start30dDate = new Date(startOfToday);
        start30dDate.setDate(start30dDate.getDate() - 30);
        const start30d = Timestamp.fromDate(start30dDate);

        const overdueDaysThreshold = 7;
        const overdueCutoffDate = new Date(startOfToday);
        overdueCutoffDate.setDate(overdueCutoffDate.getDate() - overdueDaysThreshold);
        const overdueCutoff = Timestamp.fromDate(overdueCutoffDate);

        const toDate = (ts: any): Date | null => (ts?.toDate ? ts.toDate() : null);

        const normSource = (v: any) => String(v || '').trim().toLowerCase().replace(/\s+/g, ' ');
        const sourceLabel = (v: any) => {
          const n = normSource(v);
          if (!n) return 'Unknown';
          if (n === 'walk in' || n === 'walk-in') return 'Walk In';
          if (n === 'with broker' || n === 'with brocker' || n === 'broker' || n === 'brocker') return 'With Broker';
          if (n === 'social ad' || n === 'social ads' || n === 'social lead' || n === 'meta lead') return 'Social Ad';
          return String(v || 'Unknown').trim();
        };

        const leadsRef = collection(db, 'leads');

        const qLeads7d = query(leadsRef, where('createdAt', '>=', start7d), limit(5000));
        const qActs7d = query(
          collectionGroup(db, 'activities'),
          where('at', '>=', start7d),
          where('at', '<', endTomorrow),
          orderBy('at', 'desc'),
          limit(5000)
        );
        const qAppt7d = query(
          leadsRef,
          where('appointment.scheduledAt', '>=', start7d),
          where('appointment.scheduledAt', '<', endTomorrow),
          orderBy('appointment.scheduledAt', 'desc'),
          limit(5000)
        );
        const qLeads30d = query(leadsRef, where('createdAt', '>=', start30d), limit(5000));
        const qHighOverdue = query(
          leadsRef,
          where('nextFollowUpDate', '<', overdueCutoff),
          orderBy('nextFollowUpDate', 'asc'),
          limit(25)
        );

        const ts3 = new Date(startOfToday);
        ts3.setDate(ts3.getDate() - 3);
        const ts7 = new Date(startOfToday);
        ts7.setDate(ts7.getDate() - 7);
        const ts14 = new Date(startOfToday);
        ts14.setDate(ts14.getDate() - 14);
        const qNoAct14 = query(leadsRef, where('lastActivityAt', '<', Timestamp.fromDate(ts14)), orderBy('lastActivityAt', 'asc'), limit(500));
        const qNoActNull = query(leadsRef, where('lastActivityAt', '==', null), limit(500));

        const [leads7dSnap, acts7dSnap, appt7dSnap, leads30dSnap, highOverdueSnap, noAct14Snap, noActNullSnap] =
          await Promise.all([
            getDocs(qLeads7d).catch(() => null),
            getDocs(qActs7d).catch(() => null),
            getDocs(qAppt7d).catch(() => null),
            getDocs(qLeads30d).catch(() => null),
            getDocs(qHighOverdue).catch(() => null),
            getDocs(qNoAct14).catch(() => null),
            getDocs(qNoActNull).catch(() => null),
          ]);

        if (cancelled) return;

        const leads7d = (leads7dSnap?.docs || []).map((d) => ({ id: d.id, ...(d.data() as any) }));
        const activities7d = (acts7dSnap?.docs || []).map((d) => ({ id: d.id, ...(d.data() as any), __leadId: d.ref?.parent?.parent?.id || '' }));
        const appt7dLeads = (appt7dSnap?.docs || []).map((d) => ({ id: d.id, ...(d.data() as any) }));
        const leads30d = (leads30dSnap?.docs || []).map((d) => ({ id: d.id, ...(d.data() as any) }));

        // ---------- Source Performance ----------
        const sourceAgg = new Map<
          string,
          {
            key: string;
            label: string;
            newCount: number;
            closedCount: number;
            apptCount: number;
            noShowCount: number;
            closeDiffDays: number[];
          }
        >();
        const ensureSource = (label: string) => {
          const key = normSource(label) || label;
          const ex = sourceAgg.get(key);
          if (ex) return ex;
          const created = { key, label, newCount: 0, closedCount: 0, apptCount: 0, noShowCount: 0, closeDiffDays: [] as number[] };
          sourceAgg.set(key, created);
          return created;
        };

        const leadById = new Map<string, any>();
        leads7d.forEach((l) => leadById.set(String(l.id), l));
        leads30d.forEach((l) => leadById.set(String(l.id), l));

        leads7d.forEach((l) => {
          const label = sourceLabel(l.source);
          const row = ensureSource(label);
          row.newCount += 1;
          if (isClosedLike(l.status)) row.closedCount += 1;
        });

        appt7dLeads.forEach((l) => {
          const label = sourceLabel(l.source);
          const row = ensureSource(label);
          row.apptCount += 1;
          const st = String(l?.appointment?.status || '').trim().toLowerCase();
          if (st === 'no-show' || st === 'noshow') row.noShowCount += 1;
        });

        // Close events in last 7 days (by status change activity)
        const closeAtByLead = new Map<string, Date>();
        activities7d.forEach((a: any) => {
          const leadId = String(a.__leadId || '').trim();
          if (!leadId) return;
          if (!isClosedLike(a.statusTo)) return;
          const at = toDate(a.at);
          if (!at) return;
          // keep the latest close activity in range
          if (!closeAtByLead.has(leadId) || at.getTime() > (closeAtByLead.get(leadId) as Date).getTime()) {
            closeAtByLead.set(leadId, at);
          }
        });

        const missingCloseLeadIds = Array.from(closeAtByLead.keys()).filter((id) => !leadById.has(id));
        const chunks: string[][] = [];
        for (let i = 0; i < missingCloseLeadIds.length; i += 10) chunks.push(missingCloseLeadIds.slice(i, i + 10));
        if (chunks.length) {
          const fetched = await Promise.all(
            chunks.map(async (ids) => {
              const snap = await getDocs(query(leadsRef, where(documentId(), 'in', ids)));
              return snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
            })
          );
          fetched.flat().forEach((l) => leadById.set(String(l.id), l));
        }

        closeAtByLead.forEach((closeAt, leadId) => {
          const l = leadById.get(leadId);
          if (!l) return;
          const created = toDate(l.createdAt) || toDate(l.importedAt);
          if (!created) return;
          const diffMs = closeAt.getTime() - created.getTime();
          if (diffMs < 0) return;
          const diffDays = diffMs / (1000 * 60 * 60 * 24);
          const label = sourceLabel(l.source);
          const row = ensureSource(label);
          row.closeDiffDays.push(diffDays);
        });

        const sourceRows = Array.from(sourceAgg.values())
          .map((r) => {
            const avgDays =
              r.closeDiffDays.length > 0
                ? Math.round((r.closeDiffDays.reduce((a, b) => a + b, 0) / r.closeDiffDays.length) * 10) / 10
                : null;
            const conv = funnelPct(r.closedCount, r.newCount);
            const noShowPct = funnelPct(r.noShowCount, r.apptCount);
            return {
              key: r.key,
              label: r.label,
              newCount: r.newCount,
              closedCount: r.closedCount,
              conversionPct: conv,
              avgDaysToClose: avgDays,
              apptCount: r.apptCount,
              noShowCount: r.noShowCount,
              noShowPct,
            };
          })
          .sort((a, b) => b.newCount - a.newCount || a.label.localeCompare(b.label));

        const topBrokerRows: Array<{ key: string; label: string; leads: number }> = [];

        // ---------- Agent Performance ----------
        const agentAgg = new Map<
          string,
          {
            key: string;
            label: string;
            callsConnected: number;
            callsTotal: number;
            visits: number;
            internal: number;
            touched: Set<string>;
            closures: Set<string>;
            respSumMins: number;
            respN: number;
          }
        >();
        const normEmail = (v: any) => String(v || '').trim().toLowerCase();
        const agentLabelForEmail = (email: string) => {
          if (!email) return 'System Update';
          if (email.includes('@')) return emailToName.get(email) || email.split('@')[0] || email;
          return email;
        };
        const ensureAgent = (emailRaw: any) => {
          const email = normEmail(emailRaw);
          const key = email || '__system__';
          const ex = agentAgg.get(key);
          if (ex) return ex;
          const created = {
            key,
            label: email ? agentLabelForEmail(email) : 'System Update',
            callsConnected: 0,
            callsTotal: 0,
            visits: 0,
            internal: 0,
            touched: new Set<string>(),
            closures: new Set<string>(),
            respSumMins: 0,
            respN: 0,
          };
          agentAgg.set(key, created);
          return created;
        };

        // Earliest response per lead in cohort
        const cohortLeadCreatedAt = new Map<string, Date>();
        leads7d.forEach((l) => {
          const created = toDate(l.createdAt) || toDate(l.importedAt);
          if (created) cohortLeadCreatedAt.set(String(l.id), created);
        });
        const earliestResp = new Map<string, { at: Date; email: string }>();

        for (const a of activities7d as any[]) {
          const leadId = String(a.__leadId || '').trim();
          if (!leadId) continue;
          const email = normEmail(a.byEmail);
          const at = toDate(a.at);
          if (!at) continue;

          const ag = ensureAgent(email);
          ag.touched.add(leadId);
          if (a.type === 'call') {
            ag.callsTotal += 1;
            if (String(a.callOutcome || '').trim().toLowerCase() === 'connected') ag.callsConnected += 1;
          } else if (a.type === 'visit') {
            ag.visits += 1;
          } else if (a.type === 'internal') {
            ag.internal += 1;
          }
          if (isClosedLike(a.statusTo)) ag.closures.add(leadId);

          if (cohortLeadCreatedAt.has(leadId) && email) {
            const ex = earliestResp.get(leadId);
            if (!ex || at.getTime() < ex.at.getTime()) {
              earliestResp.set(leadId, { at, email });
            }
          }
        }

        earliestResp.forEach((v, leadId) => {
          const created = cohortLeadCreatedAt.get(leadId);
          if (!created) return;
          const diffMs = v.at.getTime() - created.getTime();
          if (diffMs < 0) return;
          const mins = diffMs / (1000 * 60);
          const ag = ensureAgent(v.email);
          ag.respSumMins += mins;
          ag.respN += 1;
        });

        const agentRows = Array.from(agentAgg.values())
          .filter((r) => r.key !== '__system__')
          .map((r) => ({
            key: r.key,
            label: r.label,
            callsConnected: r.callsConnected,
            callsTotal: r.callsTotal,
            visits: r.visits,
            internal: r.internal,
            leadsTouched: r.touched.size,
            closures: r.closures.size,
            overdueSharePct: null,
            avgFirstResponseMins: r.respN ? Math.round((r.respSumMins / r.respN) * 10) / 10 : null,
          }))
          .sort((a, b) => b.closures - a.closures || b.leadsTouched - a.leadsTouched || a.label.localeCompare(b.label));

        // ---------- Risk Alerts ----------
        const highOverdue = (highOverdueSnap?.docs || [])
          .map((d) => ({ id: d.id, ...(d.data() as any) }))
          .filter((l) => !isClosedLike(l.status));

        const stale: any[] = [];
        (noAct14Snap?.docs || []).forEach((d) => stale.push({ id: d.id, ...(d.data() as any) }));
        (noActNullSnap?.docs || []).forEach((d) => stale.push({ id: d.id, ...(d.data() as any) }));

        const staleUnique = new Map<string, any>();
        stale.forEach((l) => staleUnique.set(String(l.id), l));
        const staleAll = Array.from(staleUnique.values())
          .filter((l) => !isClosedLike(l.status))
          // If lead already has a future next follow-up, don't treat it as stale risk.
          .filter((l) => {
            const next = toDate(l.nextFollowUpDate);
            return !next || next.getTime() < startOfToday.getTime();
          })
          .map((l) => {
            const last = toDate(l.lastActivityAt) || toDate(l.createdAt) || toDate(l.importedAt) || toDate(l.updatedAt);
            return { ...l, __staleRef: last };
          })
          .filter((l) => l.__staleRef)
          .sort((a, b) => (a.__staleRef as Date).getTime() - (b.__staleRef as Date).getTime());

        const staleLeads = staleAll
          .filter((l) => (l.__staleRef as Date).getTime() < ts3.getTime())
          .sort((a, b) => (a.__staleRef as Date).getTime() - (b.__staleRef as Date).getTime());

        const b3 = staleAll.filter((l) => (l.__staleRef as Date).getTime() < ts3.getTime()).length;
        const b7 = staleAll.filter((l) => (l.__staleRef as Date).getTime() < ts7.getTime()).length;
        const b14 = staleAll.filter((l) => (l.__staleRef as Date).getTime() < ts14.getTime()).length;

        // Rule: non-closed leads with no follow-up are considered "today follow-up",
        // so don't show them as a risk bucket.
        const hotNoFu: any[] = [];

        if (cancelled) return;
        setSourcePerfRows(sourceRows);
        setTopBrokers(topBrokerRows);
        setAgentPerfRows(agentRows);
        setRiskHighOverdue(highOverdue);
        setRiskNoActivityBuckets({ d3: b3, d7: b7, d14: b14 });
        setRiskNoActivityLeads(staleLeads.slice(0, 25));
        setRiskHotNoFollowUpLeads(hotNoFu);
      } catch (err: any) {
        console.error('Insights load failed', err);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [dashboardSection, db, emailToName, isClosedLike, startOf7dAgo, startOfToday, startOfTomorrow, statusIdForLabel]);

  useEffect(() => {
    if (dashboardSection !== 'broker') {
      setBrokerAnalyticsRows([]);
      return;
    }
    let cancelled = false;
    const run = async () => {
      try {
        setBrokerAnalyticsRows([]);
        const start7d = Timestamp.fromDate(startOf7dAgo);

        const start30dDate = new Date(startOfToday);
        start30dDate.setDate(start30dDate.getDate() - 30);
        const start30d = Timestamp.fromDate(start30dDate);

        const leadsRef = collection(db, 'leads');
        const openStatusIds = [
          statusIdForLabel('New'),
          statusIdForLabel('Qualify'),
          statusIdForLabel('Appointment'),
          statusIdForLabel('Site Visited') || 'site-visited',
          statusIdForLabel('Negotiation'),
          statusIdForLabel('CNR'),
          statusIdForLabel('Prospect'),
          statusIdForLabel('Reopen'),
        ]
          .filter((x) => typeof x === 'string' && x.trim())
          .map((x) => String(x).trim());

        const q =
          brokerRange === '7d'
            ? query(leadsRef, where('createdAt', '>=', start7d), limit(5000))
            : brokerRange === 'open'
              ? (openStatusIds.length > 0 ? query(leadsRef, where('status', 'in', openStatusIds.slice(0, 10)), limit(5000)) : null)
              : query(leadsRef, where('createdAt', '>=', start30d), limit(5000));

        if (!q) {
          setBrokerAnalyticsRows([]);
          return;
        }

        const snap = await getDocs(q).catch(() => null);
        if (cancelled) return;
        const rows = (snap?.docs || []).map((d) => ({ id: d.id, ...(d.data() as any) }));
        const toDate = (ts: any): Date | null => (ts?.toDate ? ts.toDate() : null);

        const agencyAgg = new Map<string, { key: string; label: string; leads: number; closed: number; completed: number; list: any[] }>();
        for (const l of rows) {
          const brokerName = String((l as any).referredBrokerName || '').trim();
          const agencyName = String((l as any).referredAgencyName || '').trim();
          const agencyLabel = agencyName || brokerName;
          if (!agencyLabel) continue;
          const key = agencyLabel.toLowerCase();
          const ex = agencyAgg.get(key) || { key, label: agencyLabel, leads: 0, closed: 0, completed: 0, list: [] as any[] };
          ex.leads += 1;
          if (isClosedLike((l as any).status)) {
            ex.closed += 1;
            if (String((l as any).closeReason || '').trim().toLowerCase() === 'completed') ex.completed += 1;
          }
          ex.list.push(l);
          agencyAgg.set(key, ex);
        }

        const analytics = Array.from(agencyAgg.values())
          .sort((a, b) => b.leads - a.leads || a.label.localeCompare(b.label))
          .slice(0, 10)
          .map((b) => {
            const leads = b.list
              .slice()
              .sort((x, y) => {
                const dx = toDate((x as any)?.createdAt) || toDate((x as any)?.importedAt) || toDate((x as any)?.updatedAt);
                const dy = toDate((y as any)?.createdAt) || toDate((y as any)?.importedAt) || toDate((y as any)?.updatedAt);
                return (dy?.getTime() || 0) - (dx?.getTime() || 0);
              });
            return { key: b.key, label: b.label, total: b.leads, closed: b.closed, completed: b.completed, leads };
          });

        setBrokerAnalyticsRows(analytics);
      } catch (err: any) {
        console.error('Broker analytics load failed', err);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [brokerRange, dashboardSection, db, isClosedLike, startOf7dAgo, startOfToday, statusIdForLabel]);

  useEffect(() => {
    if (!currentUser || (dashboardSection !== 'team' && dashboardSection !== 'my-work')) {
      setTeamTodayByAssignee({});
      setTeamOverdueByAssignee({});
      return;
    }
    const agg = (rows: any[]) => {
      const out: Record<string, number> = {};
      for (const r of rows) {
        const key = resolveAssigneeLabel(r);
        out[key] = (out[key] || 0) + 1;
      }
      return out;
    };
    setTeamTodayByAssignee(agg(todayDue));
    setTeamOverdueByAssignee(agg(overdue));
  }, [currentUser, dashboardSection, overdue, resolveAssigneeLabel, todayDue]);

  useEffect(() => {
    if (!currentUser || (dashboardSection !== 'team' && dashboardSection !== 'my-work')) {
      setTeamStats([]);
      setTeamRecentByUser({});
      return;
    }
    let cancelled = false;
    const load = async () => {
      setTeamLoading(true);
      setTeamError(null);
      try {
        const start =
          teamRange === 'today'
            ? startOfToday
            : teamRange === 'yesterday'
              ? startOfYesterday
              : startOf7dAgo;
        const end = teamRange === 'yesterday' ? startOfToday : startOfTomorrow;

        const qActs = query(
          collectionGroup(db, 'activities'),
          where('at', '>=', Timestamp.fromDate(start)),
          where('at', '<', Timestamp.fromDate(end)),
          orderBy('at', 'desc'),
          limit(5000)
        );
        const snap = await getDocs(qActs);

        const perUser = new Map<
          string,
          {
            key: string;
            name: string;
            callsConnected: number;
            callsTotal: number;
            visits: number;
            internal: number;
            leads: Set<string>;
          }
        >();
        const recentByUser = new Map<string, Array<{ id: string; leadId: string; at: any } & any>>();

        const norm = (v: any) => String(v || '').trim().toLowerCase();
        const normName = (v: any) =>
          String(v || '')
            .trim()
            .toLowerCase()
            .replace(/\s+/g, ' ');
        const resolveActorEmail = (a: any) => {
          const raw = String(a.byEmail || a.createdBy || '').trim().toLowerCase();
          if (raw) return raw;
          return nameToEmail.get(normName(a.byName)) || '';
        };
        const normalizeActivityType = (a: any) => {
          const t = norm(a.type);
          if (t === 'call' || t.startsWith('call')) return 'call';
          if (t === 'visit' || t.startsWith('visit')) return 'visit';
          if (t === 'internal' || t.startsWith('internal')) return 'internal';
          return t;
        };
        const displayNameFor = (emailLower: string) => {
          const mapped = emailToName.get(emailLower);
          if (mapped) return mapped;
          if (!emailLower) return 'Unknown';
          return emailLower.includes('@') ? (emailLower.split('@')[0] || emailLower) : emailLower;
        };

        for (const d of snap.docs) {
          const a = d.data() as any;
          const byEmailRaw = resolveActorEmail(a);
          const isSystem = !byEmailRaw;
          const key = isSystem ? '__system__' : byEmailRaw;
          const name = isSystem ? 'System Update' : displayNameFor(byEmailRaw);
          const leadId = d.ref?.parent?.parent?.id || '';

          if (!perUser.has(key)) {
            perUser.set(key, { key, name, callsConnected: 0, callsTotal: 0, visits: 0, internal: 0, leads: new Set() });
          }
          const s = perUser.get(key)!;
          if (leadId) s.leads.add(leadId);

          const t = normalizeActivityType(a);
          if (t === 'call') {
            s.callsTotal += 1;
            if (norm(a.callOutcome) === 'connected') s.callsConnected += 1;
          } else if (t === 'visit') {
            s.visits += 1;
          } else if (t === 'internal') {
            s.internal += 1;
          }

          if (!recentByUser.has(key)) recentByUser.set(key, []);
          recentByUser.get(key)!.push({
            id: d.id,
            leadId,
            at: a.at,
            type: a.type || null,
            event: a.event || null,
            callOutcome: a.callOutcome || null,
            visitWith: a.visitWith || null,
            notes: Array.isArray(a.notes) ? a.notes.filter((n: any) => typeof n === 'string' && n.trim()) : null,
            statusFrom: a.statusFrom || null,
            statusTo: a.statusTo || null,
            followUpFrom: a.followUpFrom || null,
            followUpDate: a.followUpDate || null,
            byEmail: byEmailRaw || null,
          });
        }

        let out = Array.from(perUser.entries())
          .map(([key, s]) => ({
            key,
            name: s.name,
            callsConnected: s.callsConnected,
            callsTotal: s.callsTotal,
            visits: s.visits,
            internal: s.internal,
            leadsTouched: s.leads.size,
          }))
          .sort((a, b) => (b.callsTotal + b.visits + b.internal) - (a.callsTotal + a.visits + a.internal));

        const roleByEmail = new Map<string, string>();
        for (const u of userDirectory) {
          const email = String((u as any)?.email || '').trim().toLowerCase();
          const r = String((u as any)?.roleId || (u as any)?.role || '').trim().toLowerCase();
          if (email && r) roleByEmail.set(email, r);
        }
        const meEmail = String(currentUser?.email || '').trim().toLowerCase();
        if (isSalesTeam) {
          out = meEmail ? out.filter((r) => r.key === meEmail) : [];
          recentByUser.forEach((_rows, key) => {
            if (key !== meEmail) recentByUser.delete(key);
          });
        } else if (isSalesManager) {
          const allowed = new Set<string>();
          roleByEmail.forEach((r, email) => {
            if (r === 'sales-team' || r === 'sales-manager' || r === 'salesteam' || r === 'salesmanager') {
              allowed.add(email);
            }
          });
          if (meEmail) allowed.add(meEmail);
          out = out.filter((r) => allowed.has(r.key));
          recentByUser.forEach((_rows, key) => {
            if (!allowed.has(key)) recentByUser.delete(key);
          });
        }

        if (cancelled) return;
        setTeamStats(out);
        setTeamRecentByUser(Object.fromEntries(recentByUser.entries()));
      } catch (err: any) {
        if (cancelled) return;
        console.error('dashboard activities load failed', err);
        setTeamError(err?.message || 'Failed to load activities.');
        setTeamStats([]);
        setTeamRecentByUser({});
      } finally {
        if (!cancelled) setTeamLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [
    currentUser,
    dashboardSection,
    db,
    emailToName,
    isSalesManager,
    isSalesTeam,
    nameToEmail,
    startOf7dAgo,
    startOfToday,
    startOfTomorrow,
    startOfYesterday,
    teamRange,
    userDirectory,
  ]);

  const clearWorkDoneUser = useCallback(() => {
    setWorkDoneExpanded(false);
    setWorkDoneUserKey(null);
    setWorkDoneVisible(10);
  }, []);

  const workDoneRows = useMemo(() => {
    const key = workDoneUserKey || '';
    const rows = teamRecentByUser[key] || [];
    return rows.slice(0, workDoneVisible);
  }, [teamRecentByUser, workDoneUserKey, workDoneVisible]);

  const workDoneSelectedName = useMemo(() => {
    const key = workDoneUserKey || '';
    if (!key) return '';
    const row = teamStats.find((r) => r.key === key);
    return row?.name || key;
  }, [teamStats, workDoneUserKey]);
  const teamMetricLeadIdsByUser = useMemo(() => {
    const out: Record<
      string,
      {
        callsTotal: string[];
        callsConnected: string[];
        visits: string[];
        internal: string[];
        leadsTouched: string[];
      }
    > = {};
    Object.entries(teamRecentByUser || {}).forEach(([userKey, rows]) => {
      const callsTotal = new Set<string>();
      const callsConnected = new Set<string>();
      const visits = new Set<string>();
      const internal = new Set<string>();
      const touched = new Set<string>();
      (rows || []).forEach((a: any) => {
        const leadId = String(a?.leadId || '').trim();
        if (!leadId) return;
        touched.add(leadId);
        const t = String(a?.type || '').trim().toLowerCase();
        if (t === 'call') {
          callsTotal.add(leadId);
          if (String(a?.callOutcome || '').trim().toLowerCase() === 'connected') {
            callsConnected.add(leadId);
          }
        } else if (t === 'visit') {
          visits.add(leadId);
        } else if (t === 'internal') {
          internal.add(leadId);
        }
      });
      out[userKey] = {
        callsTotal: Array.from(callsTotal),
        callsConnected: Array.from(callsConnected),
        visits: Array.from(visits),
        internal: Array.from(internal),
        leadsTouched: Array.from(touched),
      };
    });
    return out;
  }, [teamRecentByUser]);
  const teamMetricRows = useMemo(() => {
    const userKey = teamMetricOpen?.userKey || '';
    const metric = teamMetricOpen?.metric || '';
    if (!userKey || !metric) return [];
    const ids = (teamMetricLeadIdsByUser[userKey]?.[metric as keyof (typeof teamMetricLeadIdsByUser)[string]] ||
      []) as string[];
    return ids.slice(0, teamMetricVisible).map((id) => leadCache[id] || { id, name: id });
  }, [leadCache, teamMetricLeadIdsByUser, teamMetricOpen, teamMetricVisible]);
  const teamMetricTotal = useMemo(() => {
    const userKey = teamMetricOpen?.userKey || '';
    const metric = teamMetricOpen?.metric || '';
    if (!userKey || !metric) return 0;
    const ids = (teamMetricLeadIdsByUser[userKey]?.[metric as keyof (typeof teamMetricLeadIdsByUser)[string]] ||
      []) as string[];
    return ids.length;
  }, [teamMetricLeadIdsByUser, teamMetricOpen]);
  const teamMetricLabel = useMemo(() => {
    const metric = teamMetricOpen?.metric || '';
    if (metric === 'callsTotal') return 'Calls';
    if (metric === 'callsConnected') return 'Connected';
    if (metric === 'visits') return 'Visits';
    if (metric === 'internal') return 'Internal';
    if (metric === 'leadsTouched') return 'Leads';
    return 'Leads';
  }, [teamMetricOpen]);
  const teamMetricUserName = useMemo(() => {
    const userKey = teamMetricOpen?.userKey || '';
    if (!userKey) return '';
    const row = teamStats.find((r) => r.key === userKey);
    return row?.name || userKey;
  }, [teamMetricOpen, teamStats]);

  const InlineWorkDoneDetails = useCallback(
    (props: { userKey: string }) => {
      const { userKey } = props;
      const total = (teamRecentByUser[userKey] || []).length;
      const shown = Math.min(workDoneVisible, total);
      const statusLabelForActivity = (val?: string | null) => {
        const raw = String(val || '').trim();
        if (!raw) return '';
        return statusLabelMap.get(raw.toLowerCase()) || raw;
      };
      return (
        <Box sx={{ px: 1.5, pb: 1.5, pt: 0.75, bgcolor: 'grey.50' }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 900 }} noWrap>
              Recent activities — {workDoneSelectedName}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Chip
              size="small"
              label={`${shown}/${total}`}
              variant="outlined"
              sx={{ height: 22, '& .MuiChip-label': { fontWeight: 800, fontSize: 11 } }}
            />
          </Stack>

          {leadCacheLoading && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary', mb: 1 }}>
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: 12 }}>Loading lead details…</Typography>
            </Stack>
          )}

          {(workDoneRows || []).length === 0 ? (
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>No activity.</Typography>
          ) : (
            <Stack spacing={1}>
              {workDoneRows.map((a) => {
                const lead = a.leadId ? leadCache[a.leadId] : null;
                const when = a.at?.toDate ? a.at.toDate() : null;
                const leadTitle = lead?.name ? String(lead.name) : (a.leadId || '—');
                const followFrom = a.followUpFrom?.toDate ? a.followUpFrom.toDate() : null;
                const followTo = a.followUpDate?.toDate ? a.followUpDate.toDate() : null;
                const activityTypeLabel = String(a.type || 'Activity')
                  .replace(/(^|\s)\S/g, (m) => m.toUpperCase());
                const leadStatusRaw = String((lead as any)?.status || '').trim();
                const leadStatusLabel = leadStatusRaw
                  ? statusLabelMap.get(leadStatusRaw.toLowerCase()) || leadStatusRaw
                  : '—';
                const assignedLabelForLead = resolveAssigneeLabel(lead);
                return (
                  <Box key={a.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                    <LeadCompactCard
                      lead={(lead || { id: a.leadId || a.id, name: leadTitle, source: '—', assignedTo: null }) as any}
                      view="sheet"
                      showMeta={false}
                      selected={detailLeadId === (a.leadId || '')}
                      onClick={() => {
                        if (!a.leadId) return;
                        const l = leadCache[a.leadId] || { id: a.leadId, name: leadTitle };
                        openLead({ id: a.leadId, ...(l || {}) });
                      }}
                      statusLabel={leadStatusLabel}
                      assignedLabel={assignedLabelForLead}
                      badge={null}
                    />
                    <Box sx={{ px: 1.25, py: 1, bgcolor: 'background.paper' }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.75 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                          {activityTypeLabel}
                          {a.callOutcome ? ` (${a.callOutcome})` : ''}
                          {a.visitWith ? ` (${a.visitWith})` : ''}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                          {when ? when.toLocaleString() : '—'}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                        {a.event ? (
                          <Chip size="small" label={`Event: ${String(a.event).replace(/-/g, ' ')}`} sx={{ height: 22, '& .MuiChip-label': { fontSize: 11 } }} />
                        ) : null}
                        {a.statusTo ? (
                          <Chip
                            size="small"
                            label={`Status: ${statusLabelForActivity(a.statusFrom) || '—'} → ${statusLabelForActivity(a.statusTo)}`}
                            sx={{ height: 22, '& .MuiChip-label': { fontSize: 11 } }}
                          />
                        ) : null}
                        {followTo ? (
                          <Chip
                            size="small"
                            label={`Next: ${followFrom ? followFrom.toLocaleDateString() : '—'} → ${followTo.toLocaleDateString()}`}
                            sx={{ height: 22, '& .MuiChip-label': { fontSize: 11 } }}
                          />
                        ) : null}
                        {(a.notes || []).map((note, idx) => (
                          <Chip
                            key={`${a.id}-note-${idx}`}
                            size="small"
                            label={note}
                            variant="outlined"
                            sx={{
                              maxWidth: '100%',
                              height: 22,
                              '& .MuiChip-label': {
                                fontSize: 11,
                                whiteSpace: 'normal',
                              },
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}

          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
            <Button size="small" onClick={clearWorkDoneUser}>
              Clear
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={workDoneVisible >= total}
              onClick={() => setWorkDoneVisible((v) => v + 10)}
            >
              Load more
            </Button>
          </Stack>
        </Box>
      );
    },
    [
      clearWorkDoneUser,
      detailLeadId,
      leadCache,
      leadCacheLoading,
      openLead,
      resolveAssigneeLabel,
      statusLabelMap,
      teamRecentByUser,
      workDoneRows,
      workDoneSelectedName,
      workDoneVisible,
    ]
  );

  useEffect(() => {
    const key = workDoneUserKey || '';
    if (!key) return;
    const rows = workDoneRows;
    const missing = Array.from(new Set(rows.map((r) => r.leadId).filter(Boolean))).filter((id) => !leadCache[id]);
    if (missing.length === 0) return;

    let cancelled = false;
    const run = async () => {
      setLeadCacheLoading(true);
      try {
        const fetched: Record<string, any> = {};
        await Promise.all(
          missing.map(async (leadId) => {
            try {
              const snap = await getDoc(doc(db, 'leads', leadId));
              if (snap.exists()) fetched[leadId] = { id: snap.id, ...(snap.data() as any) };
              else fetched[leadId] = { id: leadId, name: '—' };
            } catch {
              fetched[leadId] = { id: leadId, name: '—' };
            }
          })
        );
        if (cancelled) return;
        setLeadCache((prev) => ({ ...prev, ...fetched }));
      } finally {
        if (!cancelled) setLeadCacheLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [db, leadCache, workDoneRows, workDoneUserKey]);
  useEffect(() => {
    const userKey = teamMetricOpen?.userKey || '';
    const metric = teamMetricOpen?.metric || '';
    if (!userKey || !metric) return;
    const ids = (teamMetricLeadIdsByUser[userKey]?.[metric as keyof (typeof teamMetricLeadIdsByUser)[string]] ||
      []) as string[];
    const needed = ids.slice(0, teamMetricVisible).filter((id) => !leadCache[id]);
    if (needed.length === 0) return;
    let cancelled = false;
    const run = async () => {
      setLeadCacheLoading(true);
      try {
        const fetched: Record<string, any> = {};
        await Promise.all(
          needed.map(async (leadId) => {
            try {
              const snap = await getDoc(doc(db, 'leads', leadId));
              if (snap.exists()) fetched[leadId] = { id: snap.id, ...(snap.data() as any) };
              else fetched[leadId] = { id: leadId, name: '—' };
            } catch {
              fetched[leadId] = { id: leadId, name: '—' };
            }
          })
        );
        if (cancelled) return;
        setLeadCache((prev) => ({ ...prev, ...fetched }));
      } finally {
        if (!cancelled) setLeadCacheLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [db, leadCache, teamMetricLeadIdsByUser, teamMetricOpen, teamMetricVisible]);

  const statusLabel = useCallback(
    (lead: any) => {
      const raw = String(lead?.status || '').trim();
      if (!raw) return '—';
      return statusLabelMap.get(raw.toLowerCase()) || raw;
    },
    [statusLabelMap]
  );
  const assignedLabel = useCallback(
    (lead: any) => {
      return resolveAssigneeLabel(lead);
    },
    [resolveAssigneeLabel]
  );

  const KpiCard = useCallback(
    (props: {
      icon: React.ReactNode;
      label: string;
      value: number | null;
      color?: 'primary' | 'error' | 'warning' | 'info' | 'success';
      onClick?: () => void;
    }) => {
      const { icon, label, value, color = 'primary', onClick } = props;
      return (
        <Paper
          onClick={onClick}
          sx={{
            p: 1.5,
            cursor: onClick ? 'pointer' : 'default',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 'none',
            minWidth: 0,
            '&:hover': onClick ? { borderColor: `${color}.main` } : undefined,
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                bgcolor: (theme) => alpha((theme as any).palette[color].main, 0.12),
                color: `${color}.main`,
              }}
            >
              {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{label}</Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.15 }}>
                {value == null ? <Skeleton width={32} /> : value}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      );
    },
    []
  );
  const kpiItems = useMemo(
    () =>
      [
        { key: 'newToday', icon: <TrendingUpOutlinedIcon fontSize="small" />, label: 'New Today', value: countNewToday, color: 'info' as const },
        {
          key: 'dueToday',
          icon: <EventAvailableOutlinedIcon fontSize="small" />,
          label: 'Follow-ups Due Today',
          value: countDueToday,
          color: 'primary' as const,
        },
        { key: 'overdue', icon: <ErrorOutlineOutlinedIcon fontSize="small" />, label: 'Overdue', value: countOverdue, color: 'error' as const },
        {
          key: 'apptToday',
          icon: <NotificationsNoneOutlinedIcon fontSize="small" />,
          label: 'Appointments Today',
          value: countApptToday,
          color: 'success' as const,
        },
        ...(isTopAdmin
          ? [
              {
                key: 'closed7d',
                icon: <TrendingUpOutlinedIcon fontSize="small" />,
                label: 'Closed (7 days)',
                value: countClosed7d,
                color: 'warning' as const,
              },
              {
                key: 'avgClose',
                icon: <TrendingUpOutlinedIcon fontSize="small" />,
                label: 'Avg Days to Close',
                value: avgDaysToCloseCompleted7d,
                color: 'warning' as const,
              },
              {
                key: 'unassigned',
                icon: <GroupsOutlinedIcon fontSize="small" />,
                label: 'Unassigned Leads',
                value: countUnassigned,
                color: 'warning' as const,
                onClick: openUnassignedFromKpi,
              },
            ]
          : []),
      ] as Array<{
        key: string;
        icon: React.ReactNode;
        label: string;
        value: number | null;
        color: 'primary' | 'error' | 'warning' | 'info' | 'success';
        onClick?: () => void;
      }>,
    [
      avgDaysToCloseCompleted7d,
      countApptToday,
      countClosed7d,
      countDueToday,
      countNewToday,
      countOverdue,
      countUnassigned,
      isTopAdmin,
      openUnassignedFromKpi,
    ]
  );
  const kpiColumns = isTopAdmin ? 7 : kpiItems.length;

  const InsightHeader = useCallback(
    (props: { icon: React.ReactNode; title: string; subtitle: string; color: any; right?: React.ReactNode }) => {
      const { icon, title, subtitle, color, right } = props;
      return (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 1, sm: 0 }}
          sx={{ mb: 1 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                bgcolor: (theme) => alpha((theme as any).palette[color].main, 0.14),
                color: `${color}.main`,
              }}
            >
              {icon}
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 900, fontSize: 14, lineHeight: 1.1 }}>{title}</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{subtitle}</Typography>
            </Box>
          </Stack>
          {right ? <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>{right}</Box> : null}
        </Stack>
      );
    },
    []
  );

  const insightPaperSx = useCallback(
    (color: any) => ({
      mb: 2,
      p: 1.5,
      borderRadius: 3,
      border: '1px solid',
      borderColor: (theme: any) => alpha(theme.palette[color].main, 0.22),
      bgcolor: (theme: any) => alpha(theme.palette[color].main, 0.04),
      boxShadow: '0 1px 0 rgba(15, 23, 42, 0.04)',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: 4,
        bgcolor: `${color}.main`,
        opacity: 0.55,
      },
    }),
    []
  );

  const SectionHeader = useCallback(
    (props: {
      icon: React.ReactNode;
      title: string;
      subtitle?: string;
      color: 'primary' | 'error' | 'warning' | 'info' | 'success' | 'secondary';
    }) => {
      const { icon, title, subtitle, color } = props;
      return (
        <Box
          sx={{
            px: 1.5,
            py: 1.25,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              right: 0,
              height: 4,
              background: (theme) =>
                `linear-gradient(90deg, ${alpha((theme as any).palette[color].main, 0.95)} 0%, ${alpha(
                  (theme as any).palette[color].main,
                  0.12
                )} 70%, transparent 100%)`,
            },
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                bgcolor: (theme) => alpha((theme as any).palette[color].main, 0.12),
                color: `${color}.main`,
              }}
            >
              {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 800, letterSpacing: 0.2 }}>{title}</Typography>
              {subtitle ? (
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{subtitle}</Typography>
              ) : null}
            </Box>
          </Stack>
        </Box>
      );
    },
    []
  );

  const SubHeader = useCallback(
    (props: {
      icon: React.ReactNode;
      title: string;
      count: number;
      color: 'primary' | 'error' | 'warning' | 'info' | 'success' | 'secondary';
    }) => {
      const { icon, title, count, color } = props;
      return (
        <Box
          sx={{
            px: 1.5,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            bgcolor: (theme) => alpha((theme as any).palette[color].main, 0.06),
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            <Box sx={{ color: `${color}.main`, display: 'inline-flex' }}>{icon}</Box>
            <Typography sx={{ fontSize: 12.5, fontWeight: 900, letterSpacing: 0.6, textTransform: 'uppercase' }}>
              {title}
            </Typography>
          </Stack>
          <Chip
            size="small"
            label={count}
            color={color === 'secondary' ? 'default' : (color as any)}
            variant={color === 'secondary' ? 'outlined' : 'filled'}
            sx={{ height: 22, '& .MuiChip-label': { fontWeight: 800 } }}
          />
        </Box>
      );
    },
    []
  );

  const WorkAccordion = useCallback(
    (props: {
      icon: React.ReactNode;
      title: string;
      count: number;
      color: 'primary' | 'error' | 'warning' | 'info' | 'success' | 'secondary';
      children: React.ReactNode;
      expanded: boolean;
      onToggle: () => void;
    }) => {
      const { icon, title, count, color, children, expanded, onToggle } = props;
      return (
        <Accordion
          expanded={expanded}
          onChange={onToggle}
          disableGutters
          elevation={0}
          square
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: `${color}.main` }} />}
            sx={{
              minHeight: 0,
              py: 0.25,
              '& .MuiAccordionSummary-content': { my: 0 },
              bgcolor: (theme) => alpha((theme as any).palette[color].main, 0.06),
            }}
          >
            <Box sx={{ width: '100%' }}>
              <SubHeader icon={icon} title={title} count={count} color={color} />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0, backgroundColor: 'background.paper' }}>{children}</AccordionDetails>
        </Accordion>
      );
    },
    [SubHeader]
  );

  const daysSince = useCallback((d: Date) => {
    const ms = Date.now() - d.getTime();
    return Math.floor(ms / (1000 * 60 * 60 * 24));
  }, []);

  const overdueShareByAgent = useMemo(() => {
    const map = new Map<string, { due: number; over: number }>();
    const bump = (lead: any, field: 'due' | 'over') => {
      const email = effectiveAssigneeEmail(lead);
      const key = email || '__unassigned__';
      const ex = map.get(key) || { due: 0, over: 0 };
      ex[field] += 1;
      map.set(key, ex);
    };
    todayDue.forEach((l) => bump(l, 'due'));
    overdue.forEach((l) => bump(l, 'over'));
    const pct = new Map<string, number>();
    map.forEach((v, k) => {
      const den = v.due + v.over;
      if (!den) return;
      pct.set(k, Math.round((v.over / den) * 100));
    });
    return pct;
  }, [effectiveAssigneeEmail, overdue, todayDue]);

  const monthDailyMonthOptions = useMemo(() => monthDailyMonthOptionsList(), []);

  const closeAnalysisRowsFromDetail = useCallback((detail: Record<string, Record<string, number>>) => {
    return Object.entries(detail || {})
      .map(([key, leadCounts]) => ({
        key,
        count: Object.values(leadCounts || {}).reduce((a, b) => a + (Number(b) || 0), 0),
      }))
      .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
  }, []);
  const closePrimaryRows = useMemo(
    () => closeAnalysisRowsFromDetail(closeAnalysisPrimaryDetail),
    [closeAnalysisPrimaryDetail, closeAnalysisRowsFromDetail]
  );
  const closeStageRows = useMemo(
    () => closeAnalysisRowsFromDetail(closeAnalysisStageDetail),
    [closeAnalysisRowsFromDetail, closeAnalysisStageDetail]
  );
  const closeAnalysisOpenInfo = useMemo(() => {
    if (!closeAnalysisOpen) return { counts: {} as Record<string, number>, total: 0 };
    const groupMap =
      closeAnalysisOpen.group === 'primary'
        ? closeAnalysisPrimaryDetail
        : closeAnalysisOpen.group === 'stage'
          ? closeAnalysisStageDetail
          : closeAnalysisOpen.group === 'budget'
            ? closeAnalysisBudgetDetail
            : closeAnalysisCompetitorDetail;
    const counts = (groupMap?.[closeAnalysisOpen.key] || {}) as Record<string, number>;
    const total = Object.values(counts).reduce((a, b) => a + (Number(b) || 0), 0);
    return { counts, total };
  }, [
    closeAnalysisCompetitorDetail,
    closeAnalysisBudgetDetail,
    closeAnalysisOpen,
    closeAnalysisPrimaryDetail,
    closeAnalysisStageDetail,
  ]);
  const closeAnalysisOpenLeads = useMemo(() => {
    return Object.entries(closeAnalysisOpenInfo.counts || {})
      .map(([leadId, count]) => ({
        leadId,
        count: Number(count) || 0,
        lead: closeAnalysisLeadCache?.[leadId] || null,
      }))
      .filter((x) => x.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [closeAnalysisLeadCache, closeAnalysisOpenInfo.counts]);
  const closeAnalysisOpenSubRows = useMemo(() => {
    if (!closeAnalysisOpen || closeAnalysisOpen.group !== 'primary') return [];
    const detail = closeAnalysisSubDetail?.[closeAnalysisOpen.key] || {};
    return Object.entries(detail)
      .map(([key, leadCounts]) => ({
        key,
        count: Object.values(leadCounts || {}).reduce((a, b) => a + (Number(b) || 0), 0),
      }))
      .filter((row) => row.count > 0)
      .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
  }, [closeAnalysisOpen, closeAnalysisSubDetail]);
  const closeAnalysisOpenSourceRows = useMemo<Array<{ key: string; count: number }>>(() => {
    if (!closeAnalysisOpen || closeAnalysisOpen.group !== 'primary') return [];
    const labelForSource = (raw: any) => {
      const s = String(raw || '').trim().toLowerCase().replace(/\s+/g, ' ');
      if (!s) return 'Unknown';
      if (s === 'walk in' || s === 'walk-in') return 'Walk In';
      if (s === 'with broker' || s === 'with brocker' || s === 'broker' || s === 'brocker') return 'With Broker';
      if (s === 'social ad' || s === 'social ads' || s === 'social lead' || s === 'meta lead') return 'Social Ad';
      if (s === 'whatsapp' || s === 'whats app' || s === 'whatsapp lead' || s === 'wa') return 'WhatsApp';
      return String(raw || '').trim() || 'Unknown';
    };
    const agg = new Map<string, number>();
    closeAnalysisOpenLeads.forEach((x) => {
      const src = labelForSource((x.lead as any)?.source);
      agg.set(src, (agg.get(src) || 0) + (x.count || 0));
    });
    return Array.from(agg.entries())
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
  }, [closeAnalysisOpen, closeAnalysisOpenLeads]);
  const closeAnalysisOpenSourceDecisionStageRows = useMemo<
    Record<string, Array<{ key: string; count: number }>>
  >(() => {
    if (!closeAnalysisOpen || closeAnalysisOpen.group !== 'primary') return {};
    const labelForSource = (raw: any) => {
      const s = String(raw || '').trim().toLowerCase().replace(/\s+/g, ' ');
      if (!s) return 'Unknown';
      if (s === 'walk in' || s === 'walk-in') return 'Walk In';
      if (s === 'with broker' || s === 'with brocker' || s === 'broker' || s === 'brocker') return 'With Broker';
      if (s === 'social ad' || s === 'social ads' || s === 'social lead' || s === 'meta lead') return 'Social Ad';
      if (s === 'whatsapp' || s === 'whats app' || s === 'whatsapp lead' || s === 'wa') return 'WhatsApp';
      return String(raw || '').trim() || 'Unknown';
    };
    const labelForDecisionStage = (lead: any) => {
      const stage = String(
        lead?.closeDecisionStage ||
          lead?.closeDecisionStageAuto ||
          lead?.closedAnalysis?.decisionStage ||
          lead?.closedAnalysis?.decisionStageAuto ||
          ''
      ).trim();
      return stage || 'Unknown';
    };
    const sourceStageMap = new Map<string, Map<string, number>>();
    closeAnalysisOpenLeads.forEach((x) => {
      const source = labelForSource((x.lead as any)?.source);
      const stage = labelForDecisionStage(x.lead);
      const stageMap = sourceStageMap.get(source) || new Map<string, number>();
      stageMap.set(stage, (stageMap.get(stage) || 0) + (x.count || 0));
      sourceStageMap.set(source, stageMap);
    });
    const out: Record<string, Array<{ key: string; count: number }>> = {};
    sourceStageMap.forEach((stageMap, source) => {
      out[source] = Array.from(stageMap.entries())
        .map(([key, count]) => ({ key, count }))
        .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
    });
    return out;
  }, [closeAnalysisOpen, closeAnalysisOpenLeads]);
  useEffect(() => {
    setCloseAnalysisOpenSourceKey(null);
  }, [closeAnalysisOpen]);
  const visibleDashboardSections = useMemo(
    () =>
      [
        { key: 'lead-visit', label: 'Lead & Visit', visible: isAdminish },
        { key: 'close-analysis', label: 'Close Analysis', visible: isTopAdmin || isSalesManager },
        { key: 'pipeline', label: 'Pipeline', visible: isTopAdmin },
        { key: 'funnel', label: 'Funnel', visible: isTopAdmin },
        { key: 'source', label: 'Source', visible: isTopAdmin },
        { key: 'broker', label: 'Broker', visible: isTopAdmin },
        { key: 'agent', label: 'Agent', visible: isTopAdmin },
        { key: 'sales-journey', label: 'Sales Journey', visible: canViewSalesJourney },
        {
          key: 'risk',
          label: 'Risk',
          visible: isTopAdmin || isSalesManager,
        },
        { key: 'my-work', label: 'My Work', visible: true },
        { key: 'new-leads', label: 'New Leads', visible: !isSalesTeam },
        { key: 'assignment-status', label: 'Assignment Status', visible: true },
        { key: 'team', label: 'Team Snapshot', visible: canViewTeamSnapshot },
      ].filter((s) => s.visible),
    [
      canViewTeamSnapshot,
      canViewSalesJourney,
      isAdminish,
      isSalesManager,
      isSalesTeam,
      isTopAdmin,
    ]
  );
  const showSection = useCallback((key: string) => dashboardSection === key, [dashboardSection]);
  useEffect(() => {
    if (!dashboardSection) return;
    if (visibleDashboardSections.some((s) => s.key === dashboardSection)) return;
    setDashboardSection('');
  }, [dashboardSection, visibleDashboardSections]);

  const journeySalespersonOptions = useMemo(
    () =>
      userDirectory
        .map((u: any) => ({
          email: String(u?.email || '').trim().toLowerCase(),
          name: String(u?.name || u?.email || '').trim(),
        }))
        .filter((u) => u.email)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [userDirectory]
  );

  const journeyOpenBranchLeads = useMemo(() => {
    const ids = journeyOpenBranch?.leadIds || [];
    return ids
      .map((id) => journeyLeadCache[id] || (id ? { id, name: id } : null))
      .filter(Boolean)
      .slice(0, journeyBranchVisible);
  }, [journeyBranchVisible, journeyLeadCache, journeyOpenBranch]);

  const journeyOpenBranchTotal = journeyOpenBranch?.leadIds?.length || 0;

  const collectJourneyPaths = useCallback((node: any, path = 'assigned') => {
    const out: string[] = [];
    const children = Object.values(node?.children || {}) as any[];
    if (children.length > 0) out.push(path);
    children.forEach((child: any) => {
      out.push(...collectJourneyPaths(child, `${path}/${child.key}`));
    });
    return out;
  }, []);

  const journeySummary = useMemo(() => {
    const root = journeyTree;
    if (!root) {
      return { assigned: 0, firstBranches: 0, rootNoFurther: 0, closedReached: 0 };
    }
    const children = Object.values(root?.children || {}) as any[];
    const childLeadIds = new Set<string>();
    children.forEach((child) => (child.leadIds || []).forEach((id: string) => childLeadIds.add(id)));
    const closedKey =
      journeyStatusOptions.find((s) => s.label.toLowerCase() === 'closed')?.key ||
      journeyStatusOptions.find((s) => s.key === 'closed')?.key ||
      '';
    const closedLeadIds = new Set<string>();
    const scan = (node: any) => {
      if (!node) return;
      if (closedKey && node.key === closedKey) {
        (node.leadIds || []).forEach((id: string) => closedLeadIds.add(id));
      }
      Object.values(node.children || {}).forEach((child: any) => scan(child));
    };
    scan(root);
    return {
      assigned: (root.leadIds || []).length,
      firstBranches: children.length,
      rootNoFurther: (root.leadIds || []).filter((id: string) => !childLeadIds.has(id)).length,
      closedReached: closedLeadIds.size,
    };
  }, [journeyStatusOptions, journeyTree]);

  const journeyHierarchyRows = useMemo(() => {
    if (!journeyTree) return [];
    const rows: Array<
      Array<{
        node: any;
        path: string;
        parentPath: string;
        depth: number;
        parentLabel: string;
        parentTotal: number;
        isNoFurther?: boolean;
        leadIds: string[];
        label: string;
      }>
    > = [];
    const pushRow = (depth: number, item: any) => {
      if (!rows[depth]) rows[depth] = [];
      rows[depth].push(item);
    };
    const walk = (node: any, path: string, depth: number, parentLabel = '', parentTotal = 0, parentPath = '') => {
      pushRow(depth, {
        node,
        path,
        parentPath,
        depth,
        parentLabel,
        parentTotal,
        leadIds: node?.leadIds || [],
        label: node?.label || '—',
      });
      if (journeyExpanded[path] !== true) return;
      const children = Object.values(node?.children || {}) as any[];
      const childLeadIds = new Set<string>();
      children.forEach((child) => (child.leadIds || []).forEach((id: string) => childLeadIds.add(id)));
      children.forEach((child: any) => walk(child, `${path}/${child.key}`, depth + 1, node.label, (node.leadIds || []).length, path));
      const noFurtherLeadIds = (node?.leadIds || []).filter((id: string) => !childLeadIds.has(id));
      if (noFurtherLeadIds.length) {
        pushRow(depth + 1, {
          node: null,
          path: `${path}/__no_further__`,
          parentPath: path,
          depth: depth + 1,
          parentLabel: node.label,
          parentTotal: (node.leadIds || []).length,
          isNoFurther: true,
          leadIds: noFurtherLeadIds,
          label: 'No further movement',
        });
      }
    };
    walk(journeyTree, 'assigned', 0);
    return rows;
  }, [journeyExpanded, journeyTree]);

  const journeyNodeColor = useCallback(
    (item: any) => {
      if (item?.isNoFurther) return '#cbd5e1';
      const statusMeta = item?.node ? journeyStatusOptions.find((s) => s.key === item.node?.key) : null;
      return statusMeta?.color || (item?.depth === 0 ? '#9d6f32' : '#94a3b8');
    },
    [journeyStatusOptions]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('salesJourneyViewMode', journeyViewMode);
  }, [journeyViewMode]);

  const journeyFocusPaths = useMemo(
    () =>
      Object.entries(journeyExpanded)
        .filter(([path, expanded]) => expanded === true && path !== 'assigned')
        .map(([path]) => path),
    [journeyExpanded]
  );

  const isJourneyPathFocused = useCallback(
    (path: string) => {
      if (journeyFocusPaths.length === 0) return true;
      return journeyFocusPaths.some(
        (focusPath) => path === focusPath || path.startsWith(`${focusPath}/`) || focusPath.startsWith(`${path}/`)
      );
    },
    [journeyFocusPaths]
  );

  useLayoutEffect(() => {
    if (journeyViewMode !== 'hierarchy') {
      setJourneyConnectors([]);
      setJourneyConnectorSize({ width: 0, height: 0 });
      return;
    }
    const canvas = journeyHierarchyCanvasRef.current;
    if (!canvas) return;

    let frame = 0;
    const measure = () => {
      const rootRect = canvas.getBoundingClientRect();
      const next: Array<{ id: string; d: string; color: string; markerId: string; active: boolean }> = [];
      const flat = journeyHierarchyRows.flat();
      const elements = new Map<string, HTMLElement>();
      const itemsByPath = new Map<string, any>();
      flat.forEach((item: any) => itemsByPath.set(item.path, item));
      canvas.querySelectorAll('[data-journey-path]').forEach((el) => {
        const path = (el as HTMLElement).dataset.journeyPath || '';
        if (path) elements.set(path, el as HTMLElement);
      });
      flat.forEach((item: any) => {
        if (!item.parentPath) return;
        const parentEl = elements.get(item.parentPath) || null;
        const childEl = elements.get(item.path) || null;
        if (!parentEl || !childEl) return;
        const parentRect = parentEl.getBoundingClientRect();
        const childRect = childEl.getBoundingClientRect();
        const startX = parentRect.left - rootRect.left + parentRect.width / 2;
        const startY = parentRect.bottom - rootRect.top;
        const endX = childRect.left - rootRect.left + childRect.width / 2;
        const endY = childRect.top - rootRect.top;
        const gap = Math.max(24, endY - startY);
        const curveY = Math.min(gap * 0.58, 72);
        const parentItem = itemsByPath.get(item.parentPath);
        const color = journeyNodeColor(parentItem);
        const markerId = `journey-arrow-${color.replace(/[^a-zA-Z0-9]/g, '') || 'default'}`;
        next.push({
          id: item.path,
          d: `M ${startX} ${startY} C ${startX} ${startY + curveY}, ${endX} ${endY - curveY}, ${endX} ${endY - 4}`,
          color,
          markerId,
          active: isJourneyPathFocused(item.path),
        });
      });
      setJourneyConnectorSize({ width: canvas.scrollWidth, height: canvas.scrollHeight });
      setJourneyConnectors(next);
    };
    const schedule = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(measure);
    };

    schedule();
    const ro = new ResizeObserver(schedule);
    ro.observe(canvas);
    window.addEventListener('resize', schedule);
    return () => {
      window.cancelAnimationFrame(frame);
      ro.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, [isJourneyPathFocused, journeyHierarchyRows, journeyNodeColor, journeyViewMode]);

  const renderJourneyHierarchyCard = (item: any) => {
    const total = item.leadIds?.length || 0;
    const pctOfParent = item.depth > 0 && item.parentTotal ? Math.round((total / item.parentTotal) * 100) : null;
    const hasChildren = item.node && Object.keys(item.node?.children || {}).length > 0;
    const expanded = journeyExpanded[item.path] === true;
    const dotColor = journeyNodeColor(item);
    const focused = isJourneyPathFocused(item.path);
    return (
      <Box
        key={item.path}
        data-journey-path={item.path}
        sx={{
          position: 'relative',
          zIndex: 1,
          width: { xs: 190, sm: 210 },
          flex: '0 0 auto',
          border: '1px solid',
          borderColor: expanded ? alpha(dotColor, 0.42) : 'divider',
          borderRadius: 2,
          bgcolor: item.isNoFurther ? 'rgba(15, 23, 42, 0.018)' : (expanded ? alpha(dotColor, 0.06) : 'background.paper'),
          boxShadow: '0 1px 0 rgba(15, 23, 42, 0.04)',
          overflow: 'hidden',
          opacity: focused ? 1 : 0.46,
          transition: 'opacity 160ms ease, border-color 160ms ease, background-color 160ms ease',
        }}
      >
        <Box sx={{ height: 4, bgcolor: dotColor }} />
        <Box sx={{ p: 1 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: dotColor, flex: '0 0 auto' }} />
            <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 900, color: item.isNoFurther ? 'text.secondary' : 'text.primary' }}>
              {item.label}
            </Typography>
            {hasChildren && (
              <ButtonBase
                onClick={() => setJourneyExpanded((prev) => ({ ...prev, [item.path]: !prev[item.path] }))}
                sx={{ width: 24, height: 24, borderRadius: 1, color: '#9d6f32' }}
              >
                <ExpandMoreIcon sx={{ fontSize: 18, transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
              </ButtonBase>
            )}
          </Stack>
          {item.parentLabel && (
            <Typography noWrap sx={{ mt: 0.3, fontSize: 11.5, color: 'text.secondary' }}>
              from {item.parentLabel}
            </Typography>
          )}
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.8 }}>
            {pctOfParent != null && (
              <Tooltip title={`${total} of ${item.parentTotal} from ${item.parentLabel}`} placement="top" arrow>
                <Chip
                  size="small"
                  label={`${pctOfParent}%`}
                  variant="outlined"
                  sx={{ height: 22, '& .MuiChip-label': { fontSize: 11, fontWeight: 800 } }}
                />
              </Tooltip>
            )}
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setJourneyBranchVisible(20);
                setJourneyOpenBranch({ pathKey: item.path, label: item.parentLabel ? `${item.parentLabel} → ${item.label}` : item.label, leadIds: item.leadIds || [] });
              }}
              sx={{ minWidth: 58, height: 28, justifyContent: 'flex-end', fontWeight: 900, borderColor: alpha(dotColor, 0.55), color: item.isNoFurther ? 'text.secondary' : '#9d6f32' }}
            >
              {total}
            </Button>
          </Stack>
        </Box>
      </Box>
    );
  };

  const renderJourneyNode = (node: any, depth = 0, path = 'assigned', parentTotal = 0) => {
    const children = Object.values(node?.children || {}) as any[];
    const childLeadIds = new Set<string>();
    children.forEach((child) => (child.leadIds || []).forEach((id: string) => childLeadIds.add(id)));
    const noFurtherLeadIds = (node?.leadIds || []).filter((id: string) => !childLeadIds.has(id));
    const total = (node?.leadIds || []).length;
    const pctOfParent = depth > 0 && parentTotal ? Math.round((total / parentTotal) * 100) : null;
    const expanded = journeyExpanded[path] === true;
    const statusMeta = journeyStatusOptions.find((s) => s.key === node?.key);
    const dotColor = statusMeta?.color || (depth === 0 ? '#9d6f32' : '#94a3b8');
    const rows = [
      ...children.map((child) => ({ key: child.key, node: child })),
      ...(noFurtherLeadIds.length ? [{ key: '__no_further__', node: null }] : []),
    ];
    const childMath = [
      ...children.map((child: any) => `${child.leadIds?.length || 0} ${child.label}`),
      ...(noFurtherLeadIds.length ? [`${noFurtherLeadIds.length} No further movement`] : []),
    ];

    return (
      <Box
        key={path}
        sx={{
          pl: depth === 0 ? 0 : { xs: 1, sm: 1.5 },
          ml: depth === 0 ? 0 : { xs: 0.5, sm: 1 },
          borderLeft: depth === 0 ? 0 : '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            mb: 0.75,
            px: 1,
            py: 0.75,
            border: '1px solid',
            borderColor: expanded ? alpha(dotColor, 0.35) : 'divider',
            borderRadius: 2,
            bgcolor: expanded ? alpha(dotColor, 0.055) : 'background.paper',
            boxShadow: '0 1px 0 rgba(15, 23, 42, 0.03)',
            '&:hover': { borderColor: alpha(dotColor, 0.45), bgcolor: alpha(dotColor, 0.045) },
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <ButtonBase
              onClick={() => setJourneyExpanded((prev) => ({ ...prev, [path]: !prev[path] }))}
              disabled={rows.length === 0}
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1,
                color: rows.length ? '#9d6f32' : 'text.disabled',
                flex: '0 0 auto',
              }}
            >
              {rows.length ? <ExpandMoreIcon sx={{ fontSize: 19, transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)' }} /> : null}
            </ButtonBase>
            <Box
              sx={{
                width: 11,
                height: 11,
                borderRadius: '50%',
                bgcolor: dotColor,
                border: '1px solid rgba(15, 23, 42, 0.12)',
                flex: '0 0 auto',
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: depth === 0 ? 14 : 13, fontWeight: depth === 0 ? 900 : 800 }}>
                {node.label}
              </Typography>
              {expanded && childMath.length > 0 && (
                <Typography noWrap sx={{ mt: 0.2, fontSize: 11.5, color: 'text.secondary' }}>
                  {total} = {childMath.join(' + ')}
                </Typography>
              )}
            </Box>
            {pctOfParent != null && (
              <Chip
                size="small"
                label={`${pctOfParent}%`}
                variant="outlined"
                sx={{ height: 24, minWidth: 54, '& .MuiChip-label': { fontSize: 11.5, fontWeight: 800 } }}
              />
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setJourneyBranchVisible(20);
                setJourneyOpenBranch({ pathKey: path, label: node.label, leadIds: node.leadIds || [] });
              }}
              sx={{ minWidth: 64, justifyContent: 'flex-end', fontWeight: 900, borderColor: alpha(dotColor, 0.55), color: '#9d6f32' }}
            >
              {total}
            </Button>
          </Stack>
        </Box>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Stack spacing={0.25} sx={{ py: rows.length ? 0.25 : 0 }}>
            {rows.map((row) => {
              const childPath = `${path}/${row.key}`;
              if (row.node) return renderJourneyNode(row.node, depth + 1, childPath, total);
              const noFurtherPct = total ? Math.round((noFurtherLeadIds.length / total) * 100) : 0;
              return (
                <Box
                  key={childPath}
                  sx={{
                    ml: depth === 0 ? { xs: 1, sm: 1.5 } : { xs: 0.5, sm: 1 },
                    mb: 0.75,
                    px: 1,
                    py: 0.7,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'rgba(15, 23, 42, 0.018)',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 28, height: 28, flex: '0 0 auto' }} />
                    <Box
                      sx={{
                        width: 9,
                        height: 9,
                        borderRadius: '50%',
                        bgcolor: 'grey.300',
                        flex: '0 0 auto',
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary' }}>
                        No further movement
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={`${noFurtherPct}%`}
                      variant="outlined"
                      sx={{ height: 24, minWidth: 54, '& .MuiChip-label': { fontSize: 11.5, fontWeight: 800, color: 'text.secondary' } }}
                    />
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        setJourneyBranchVisible(20);
                        setJourneyOpenBranch({ pathKey: childPath, label: `${node.label} → No further movement`, leadIds: noFurtherLeadIds });
                      }}
                      sx={{ minWidth: 60, justifyContent: 'flex-end', fontWeight: 900, color: 'text.secondary' }}
                    >
                      {noFurtherLeadIds.length}
                    </Button>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Collapse>
      </Box>
    );
  };

  return (
    <>
      {(() => {
        // Reserve the full drawer width so the dashboard never goes behind the detail panel (desktop).
        const detailOffset = detailDrawerOpen ? Math.max(detailWidth, 0) : 0;
        return (
          <Box sx={{ pr: { xs: 0, sm: `${detailOffset}px` }, transition: 'padding-right 0.2s ease' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Dashboard
        </Typography>
        <Box sx={{ flex: 1 }} />
        {canAction('leads', 'addLead') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDrawerOpen(true)}
            sx={{ borderRadius: 2 }}
          >
            Add Lead
          </Button>
        )}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Signed in as {currentUser?.email || '—'}
      </Typography>

      {error && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}
      {kpiError && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {kpiError}
        </Alert>
      )}

      <Box sx={{ mb: 2, overflowX: { xs: 'auto', md: 'visible' }, pb: { xs: 0.75, md: 0 } }}>
        <Box
          sx={{
            display: 'grid',
            gap: 1,
            gridAutoFlow: { xs: 'column', md: 'row' },
            gridAutoColumns: { xs: 'minmax(230px, 82vw)', sm: 'minmax(240px, 46vw)', md: 'auto' },
            gridTemplateColumns: { xs: 'none', md: `repeat(${kpiColumns}, minmax(0, 1fr))` },
          }}
        >
          {kpiItems.map((item) => (
            <KpiCard
              key={item.key}
              icon={item.icon}
              label={item.label}
              value={item.value}
              color={item.color}
              onClick={item.onClick}
            />
          ))}
        </Box>
      </Box>

      <Stack
        direction="row"
        spacing={0.75}
        useFlexGap
        flexWrap={{ xs: 'nowrap', sm: 'wrap' }}
        sx={{
          mb: 2,
          overflowX: { xs: 'auto', sm: 'visible' },
          pb: { xs: 0.5, sm: 0 },
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(15, 23, 42, 0.25)', borderRadius: 999 },
        }}
      >
        {visibleDashboardSections.map((s) => (
          <Chip
            key={s.key}
            size="small"
            label={s.label}
            clickable
            color={dashboardSection === s.key ? 'primary' : 'default'}
            variant={dashboardSection === s.key ? 'filled' : 'outlined'}
            onClick={() => setDashboardSection(s.key)}
            sx={{ flex: '0 0 auto' }}
          />
        ))}
      </Stack>

      {!dashboardSection && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select a section chip to load dashboard analytics.
        </Alert>
      )}

      {isAdminish && showSection('lead-visit') && (
        <LeadVisitAnalyticsPanel
          active={showSection('lead-visit')}
          leadStatuses={leadStatuses}
          userDirectory={userDirectory}
          onOpenLead={openLead}
          detailLeadId={detailLeadId}
        />
      )}

      {(isTopAdmin || isSalesManager) && showSection('close-analysis') && (
        <Paper sx={insightPaperSx('primary')}>
          <InsightHeader
            icon={<ReportProblemOutlinedIcon fontSize="small" />}
            title="Close Analysis"
            subtitle="Month-wise close reason and decision-stage distribution"
            color="primary"
            right={
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <Button
                  size="small"
                  variant={closeAnalysisView === 'summary' ? 'contained' : 'outlined'}
                  onClick={() => setCloseAnalysisView('summary')}
                >
                  Summary
                </Button>
                <Button
                  size="small"
                  variant={closeAnalysisView === 'daily' ? 'contained' : 'outlined'}
                  onClick={() => setCloseAnalysisView('daily')}
                >
                  Daily
                </Button>
                <FormControl size="small" sx={{ minWidth: 140, width: { xs: '100%', sm: 'auto' } }}>
                  <Select
                    value={closeAnalysisMonthKey}
                    onChange={(e) => setCloseAnalysisMonthKey(String(e.target.value || ''))}
                  >
                    {monthDailyMonthOptions.map((m) => (
                      <MenuItem key={m.key} value={m.key}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            }
          />

          {closeAnalysisError && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {closeAnalysisError}
            </Alert>
          )}

          {closeAnalysisLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading close analysis…
              </Typography>
            </Box>
          ) : closeAnalysisView === 'daily' ? (
            closeAnalysisDailyRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No daily close data for selected month.
              </Typography>
            ) : (
              <Stack spacing={1}>
                <Box sx={{ overflowX: 'auto' }}>
                  <Box
                    sx={{
                      minWidth: { xs: 1120, md: 0 },
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      overflow: 'visible',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '120px repeat(11, minmax(0, 1fr))',
                        px: 1.25,
                        py: 0.75,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        bgcolor: (theme) => alpha((theme as any).palette.primary.main, 0.08),
                      }}
                    >
                          {['Date', 'Total', 'Completed', 'Not Interested', 'Other Location', 'Budget', 'Distance', 'No Response', 'Wrong No.', 'Broker', 'Competitor', 'Other'].map((h, idx) => (
                            <Typography
                              key={h}
                              sx={{
                                fontSize: 12,
                                fontWeight: 900,
                                color: 'text.secondary',
                                textAlign: idx === 0 ? 'left' : 'center',
                                ...(idx === 0
                                  ? {
                                      position: 'sticky',
                                      left: 0,
                                      zIndex: 6,
                                      bgcolor: 'background.paper',
                                      borderRight: '1px solid',
                                      borderColor: 'divider',
                                      boxShadow: '2px 0 0 rgba(15, 23, 42, 0.06)',
                                      pl: 0.25,
                                    }
                                  : {}),
                              }}
                            >
                              {h}
                        </Typography>
                      ))}
                    </Box>

                  {closeAnalysisDailyRows
                    .slice()
                    .reverse()
                    .map((r: any) => {
                      const renderReasonCell = (reason: string, value: number) => {
                        const active =
                          closeAnalysisDailyOpen?.dayKey === r.key &&
                          closeAnalysisDailyOpen?.reason === reason;
                        if (value <= 0) {
                          return (
                            <Typography sx={{ fontSize: 12.5, opacity: 0.7, textAlign: 'center' }}>
                              {value}
                            </Typography>
                          );
                        }
                        return (
                          <ButtonBase
                            onClick={() =>
                              setCloseAnalysisDailyOpen((prev) =>
                                prev?.dayKey === r.key && prev?.reason === reason
                                  ? null
                                  : { dayKey: r.key, reason }
                              )
                            }
                            sx={{
                              justifyContent: 'center',
                              width: '100%',
                              minHeight: 24,
                              borderRadius: 1,
                              ...(active
                                ? { bgcolor: (theme: any) => alpha(theme.palette.primary.main, 0.15) }
                                : {}),
                              '&:hover': {
                                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.10),
                              },
                            }}
                          >
                            <Typography sx={{ fontSize: 12.5, fontWeight: active ? 900 : 700, color: 'primary.dark' }}>
                              {value}
                            </Typography>
                          </ButtonBase>
                        );
                      };
                      return (
                        <React.Fragment key={r.key}>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: '120px repeat(11, minmax(0, 1fr))',
                              px: 1.25,
                              py: 0.7,
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 12.5,
                                fontWeight: 800,
                                textAlign: 'left',
                                position: 'sticky',
                                left: 0,
                                zIndex: 5,
                                bgcolor: 'background.paper',
                                borderRight: '1px solid',
                                borderColor: 'divider',
                                boxShadow: '2px 0 0 rgba(15, 23, 42, 0.06)',
                                pl: 0.25,
                              }}
                            >
                              {r.key}
                            </Typography>
                            <Typography sx={{ fontSize: 12.5, textAlign: 'center' }}>{r.totalClosed || 0}</Typography>
                            {renderReasonCell('Completed', Number(r['Completed'] || 0))}
                            {renderReasonCell('Not Interested', Number(r['Not Interested'] || 0))}
                            {renderReasonCell('Interested in Another Location', Number(r['Interested in Another Location'] || 0))}
                            {renderReasonCell('Budget / Financial Issue', Number(r['Budget / Financial Issue'] || 0))}
                            {renderReasonCell('Distance Issue', Number(r['Distance Issue'] || 0))}
                            {renderReasonCell('Not Reachable (No Response)', Number(r['Not Reachable (No Response)'] || 0))}
                            {renderReasonCell('Wrong / Invalid Number', Number(r['Wrong / Invalid Number'] || 0))}
                            {renderReasonCell('Broker Inquiry Only', Number(r['Broker Inquiry Only'] || 0))}
                            {renderReasonCell('Loss due to Competitor', Number(r['Loss due to Competitor'] || 0))}
                            {renderReasonCell('Other', Number(r.Other || 0))}
                          </Box>
                          <Collapse
                            in={Boolean(closeAnalysisDailyOpen?.dayKey === r.key && closeAnalysisDailyOpen?.reason)}
                            timeout={200}
                            unmountOnExit
                          >
                            {(() => {
                              const openReason =
                                closeAnalysisDailyOpen?.dayKey === r.key
                                  ? closeAnalysisDailyOpen?.reason || ''
                                  : '';
                              const counts = openReason
                                ? (closeAnalysisDailyDetail?.[r.key]?.[openReason] || {})
                                : {};
                              const leads = Object.entries(counts)
                                .sort((a: any, b: any) => Number(b[1] || 0) - Number(a[1] || 0))
                                .map(([leadId, count]) => ({
                                  leadId,
                                  count: Number(count || 0),
                                  lead: closeAnalysisLeadCache?.[leadId] || null,
                                }));
                              const total = leads.reduce((acc, x) => acc + (x.count || 0), 0);
                              return (
                                <Box
                                  sx={{
                                    borderTop: '1px solid',
                                    borderColor: 'divider',
                                    p: 1,
                                    bgcolor: (theme) => alpha((theme as any).palette.primary.main, 0.04),
                                  }}
                                >
                                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 900 }}>
                                      {r.key} • {openReason || 'Reason'} — {total}
                                    </Typography>
                                    <Button size="small" onClick={() => setCloseAnalysisDailyOpen(null)}>
                                      Close
                                    </Button>
                                  </Stack>
                                  {leads.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                      No leads to show.
                                    </Typography>
                                  ) : (
                                    <Stack spacing={0}>
                                      {leads.map(({ lead, leadId, count }) => (
                                        <Box key={leadId} sx={{ position: 'relative' }}>
                                          <LeadCompactCard
                                            lead={lead}
                                            view="sheet"
                                            selected={detailLeadId === leadId}
                                            onClick={() => openLead(lead)}
                                            statusLabel={statusLabel(lead)}
                                            assignedLabel={assignedLabel(lead)}
                                            badge={null}
                                          />
                                          {count > 1 && (
                                            <Chip
                                              size="small"
                                              label={`x${count}`}
                                              sx={{
                                                position: 'absolute',
                                                right: 12,
                                                top: 10,
                                                height: 18,
                                                fontSize: 11,
                                                bgcolor: (theme) => alpha((theme as any).palette.primary.main, 0.12),
                                              }}
                                            />
                                          )}
                                        </Box>
                                      ))}
                                    </Stack>
                                  )}
                                </Box>
                              );
                            })()}
                          </Collapse>
                        </React.Fragment>
                      );
                    })}
                  </Box>
                </Box>
              </Stack>
            )
          ) : (
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip size="small" label={`Total Closed: ${closeAnalysisSummary.totalClosed}`} />
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                  gap: 1,
                }}
              >
                {[
                  { title: 'Primary Reason', group: 'primary' as const, rows: closePrimaryRows },
                  { title: 'Decision Stage', group: 'stage' as const, rows: closeStageRows },
                ].map((section) => (
                  <Paper
                    key={section.group}
                    sx={{
                      p: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: 'none',
                    }}
                  >
                    <Typography sx={{ fontSize: 12.5, fontWeight: 900, mb: 0.75 }}>
                      {section.title}
                    </Typography>
                    {section.rows.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No data.
                      </Typography>
                    ) : (
                      <Stack spacing={0.5}>
                        {(section.group === 'primary' ? section.rows : section.rows.slice(0, 7)).map((row) => {
                          const active =
                            closeAnalysisOpen?.group === section.group &&
                            closeAnalysisOpen?.key === row.key;
                          const showInlinePrimaryAnalysis =
                            section.group === 'primary' &&
                            active &&
                            (closeAnalysisOpenSubRows.length > 0 || closeAnalysisOpenSourceRows.length > 0);
                          return (
                            <React.Fragment key={row.key}>
                              <ButtonBase
                                onClick={() =>
                                  setCloseAnalysisOpen((prev) =>
                                    prev?.group === section.group && prev?.key === row.key
                                      ? null
                                      : { group: section.group, key: row.key }
                                  )
                                }
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  width: '100%',
                                  px: 0.75,
                                  py: 0.45,
                                  borderRadius: 1,
                                  textAlign: 'left',
                                  ...(active
                                    ? { bgcolor: (theme: any) => alpha(theme.palette.primary.main, 0.12) }
                                  : {}),
                                '&:hover': {
                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                },
                              }}
                              >
                                <Typography sx={{ fontSize: 12.5 }}>{row.key}</Typography>
                                <Typography sx={{ fontSize: 12.5, fontWeight: 800 }}>
                                  {row.count}
                                </Typography>
                              </ButtonBase>
                              <Collapse in={showInlinePrimaryAnalysis} timeout={180} unmountOnExit>
                                <Box
                                  sx={{
                                    mt: 0.25,
                                    mb: 0.5,
                                    ml: 1,
                                    pl: 0.9,
                                    pr: 0.75,
                                    py: 0.55,
                                    borderRadius: 1,
                                    borderLeft: '2px solid',
                                    borderColor: (theme) => alpha((theme as any).palette.primary.main, 0.5),
                                    bgcolor: (theme) => alpha((theme as any).palette.primary.main, 0.08),
                                  }}
                                >
                                  {closeAnalysisOpenSubRows.length > 0 && (
                                    <>
                                      <Box
                                        sx={{
                                          border: '1px solid',
                                          borderColor: (theme) => alpha((theme as any).palette.success.main, 0.35),
                                          borderRadius: 0.9,
                                          px: 0.5,
                                          py: 0.35,
                                          bgcolor: (theme) => alpha((theme as any).palette.success.main, 0.08),
                                        }}
                                      >
                                        <Typography sx={{ fontSize: 11.5, fontWeight: 900, mb: 0.35, color: 'success.dark' }}>
                                          Sub Responses
                                        </Typography>
                                        <Stack spacing={0.2}>
                                          {closeAnalysisOpenSubRows.map((subRow) => (
                                            <Stack
                                              key={subRow.key}
                                              direction="row"
                                              alignItems="center"
                                              justifyContent="space-between"
                                              sx={{
                                                minHeight: 20,
                                                px: 0.35,
                                                borderRadius: 0.75,
                                                transition: 'background-color 120ms ease',
                                                '&:hover': {
                                                  bgcolor: (theme) => alpha((theme as any).palette.success.main, 0.16),
                                                },
                                              }}
                                            >
                                              <Typography sx={{ fontSize: 11.75, pr: 1 }}>{subRow.key}</Typography>
                                              <Typography sx={{ fontSize: 11.75, fontWeight: 800 }}>
                                                {subRow.count}
                                              </Typography>
                                            </Stack>
                                          ))}
                                        </Stack>
                                      </Box>
                                    </>
                                  )}
                                  {closeAnalysisOpenSourceRows.length > 0 && (
                                    <Box
                                      sx={{
                                        mt: 0.8,
                                        border: '1px solid',
                                        borderColor: (theme) => alpha((theme as any).palette.info.main, 0.35),
                                        borderRadius: 0.9,
                                        px: 0.5,
                                        py: 0.35,
                                        bgcolor: (theme) => alpha((theme as any).palette.info.main, 0.08),
                                      }}
                                    >
                                      <Typography sx={{ fontSize: 11.5, fontWeight: 900, mb: 0.35, color: 'info.dark' }}>
                                        Lead Source
                                      </Typography>
                                      <Stack spacing={0.2}>
                                        {closeAnalysisOpenSourceRows.map((srcRow) => (
                                          <Box key={srcRow.key}>
                                            {(() => {
                                              const stages = closeAnalysisOpenSourceDecisionStageRows[srcRow.key] || [];
                                              const canExpand = stages.length > 0;
                                              const active = closeAnalysisOpenSourceKey === srcRow.key;
                                              return (
                                                <>
                                                  <ButtonBase
                                                    onClick={() => {
                                                      if (!canExpand) return;
                                                      setCloseAnalysisOpenSourceKey((prev) =>
                                                        prev === srcRow.key ? null : srcRow.key
                                                      );
                                                    }}
                                                    sx={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'space-between',
                                                      width: '100%',
                                                      minHeight: 20,
                                                      px: 0.35,
                                                      borderRadius: 0.75,
                                                      transition: 'background-color 120ms ease',
                                                      cursor: canExpand ? 'pointer' : 'default',
                                                      ...(active
                                                        ? { bgcolor: (theme: any) => alpha(theme.palette.info.main, 0.16) }
                                                        : {}),
                                                      '&:hover': {
                                                        bgcolor: (theme) => alpha((theme as any).palette.info.main, 0.16),
                                                      },
                                                    }}
                                                  >
                                                    <Typography sx={{ fontSize: 11.75, pr: 1 }}>{srcRow.key}</Typography>
                                                    <Typography sx={{ fontSize: 11.75, fontWeight: 800 }}>
                                                      {srcRow.count}
                                                    </Typography>
                                                  </ButtonBase>
                                                  <Collapse in={canExpand && active} timeout={160} unmountOnExit>
                                                    <Box
                                                      sx={{
                                                        ml: 1.5,
                                                        mt: 0.15,
                                                        px: 0.5,
                                                        py: 0.35,
                                                        borderRadius: 0.9,
                                                        borderLeft: '2px solid',
                                                        borderColor: (theme) => alpha((theme as any).palette.primary.main, 0.5),
                                                        bgcolor: (theme) => alpha((theme as any).palette.primary.main, 0.08),
                                                      }}
                                                    >
                                                      <Typography
                                                        sx={{
                                                          fontSize: 10.75,
                                                          fontWeight: 800,
                                                          color: 'primary.dark',
                                                          mb: 0.15,
                                                        }}
                                                      >
                                                        Decision Stage
                                                      </Typography>
                                                      <Stack spacing={0.15}>
                                                        {stages.map((stageRow) => (
                                                          <Stack
                                                            key={`${srcRow.key}__${stageRow.key}`}
                                                            direction="row"
                                                            alignItems="center"
                                                            justifyContent="space-between"
                                                            sx={{
                                                              minHeight: 18,
                                                              px: 0.3,
                                                              borderRadius: 0.6,
                                                              '&:hover': {
                                                                bgcolor: (theme) => alpha((theme as any).palette.primary.main, 0.14),
                                                              },
                                                            }}
                                                          >
                                                            <Typography sx={{ fontSize: 10.9, pr: 1 }}>{stageRow.key}</Typography>
                                                            <Typography sx={{ fontSize: 10.9, fontWeight: 800 }}>
                                                              {stageRow.count}
                                                            </Typography>
                                                          </Stack>
                                                        ))}
                                                      </Stack>
                                                    </Box>
                                                  </Collapse>
                                                </>
                                              );
                                            })()}
                                          </Box>
                                        ))}
                                      </Stack>
                                    </Box>
                                  )}
                                </Box>
                              </Collapse>
                            </React.Fragment>
                          );
                        })}
                      </Stack>
                    )}
                  </Paper>
                ))}
              </Box>

              <Collapse in={Boolean(closeAnalysisOpen)} timeout={200} unmountOnExit>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    p: 1,
                    bgcolor: (theme) => alpha((theme as any).palette.primary.main, 0.04),
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 900 }}>
                      {closeAnalysisOpen?.key || 'Selection'} — {closeAnalysisOpenInfo.total}
                    </Typography>
                    <Button size="small" onClick={() => setCloseAnalysisOpen(null)}>
                      Close
                    </Button>
                  </Stack>
                  {closeAnalysisOpenLeads.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No leads to show.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      <Stack spacing={0}>
                        {closeAnalysisOpenLeads.map(({ lead, leadId, count }) => (
                          <Box key={leadId} sx={{ position: 'relative' }}>
                            <LeadCompactCard
                              lead={lead}
                              view="sheet"
                              selected={detailLeadId === leadId}
                              onClick={() => openLead(lead)}
                              statusLabel={statusLabel(lead)}
                              assignedLabel={assignedLabel(lead)}
                              badge={null}
                            />
                            {count > 1 && (
                              <Chip
                                size="small"
                                label={`x${count}`}
                                sx={{
                                  position: 'absolute',
                                  right: 12,
                                  top: 10,
                                  height: 18,
                                  fontSize: 11,
                                  bgcolor: (theme) => alpha((theme as any).palette.primary.main, 0.12),
                                }}
                              />
                            )}
                          </Box>
                        ))}
                      </Stack>
                    </Stack>
                  )}
                </Box>
              </Collapse>
            </Stack>
          )}
        </Paper>
      )}

      {isTopAdmin && showSection('pipeline') && pipelineSnapshot.length > 0 && (
        <Paper sx={insightPaperSx('info')}>
          <InsightHeader
            icon={<AccountTreeOutlinedIcon fontSize="small" />}
            title="Pipeline Snapshot"
            subtitle="Trend = entered in last 7 days"
            color="info"
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' },
              gap: 1,
            }}
          >
            {pipelineSnapshot.map((s) => (
              (() => {
                const card = (
                  <Paper
                    key={s.key}
                    sx={{
                      p: 1.25,
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: 'none',
                      overflow: 'hidden',
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.25 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 800 }}>{s.label}</Typography>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: 999,
                          bgcolor: (theme) =>
                            alpha((theme as any).palette[s.color]?.main || theme.palette.primary.main, 0.7),
                        }}
                      />
                    </Stack>
                    <Typography sx={{ fontSize: 20, fontWeight: 900, lineHeight: 1.2 }}>
                      {s.count == null ? <Skeleton width={30} /> : s.count}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {s.trend7d == null ? '7d: —' : `7d: +${s.trend7d}`}
                    </Typography>
                  </Paper>
                );

                if (s.key !== 'closed') return card;

                const reasons = Object.entries(pipelineClosedReasonCounts || {});
                const hasAny = reasons.length > 0;
                const title = (
                  <Box sx={{ p: 0.5 }}>
                    <Typography sx={{ fontWeight: 900, fontSize: 12, mb: 0.5 }}>Closed Reasons</Typography>
                    <Stack spacing={0.25}>
                      {(reasons.length ? reasons : [['(No data)', null]]).map(([reason, cnt]) => (
                        <Stack key={reason} direction="row" spacing={1} justifyContent="space-between">
                          <Typography sx={{ fontSize: 12, opacity: 0.9 }}>{reason}</Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 800 }}>
                            {cnt == null ? '—' : cnt}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                );

                return (
                  <Tooltip title={title} placement="top" arrow disableHoverListener={!hasAny}>
                    <Box sx={{ cursor: hasAny ? 'help' : 'default' }}>{card}</Box>
                  </Tooltip>
                );
              })()
            ))}
          </Box>
        </Paper>
      )}

      {isTopAdmin && showSection('funnel') && funnelOverall.newCount != null && (
        <Paper sx={insightPaperSx('secondary')}>
          <InsightHeader
            icon={<FilterAltOutlinedIcon fontSize="small" />}
            title="Funnel KPIs"
            subtitle="Cohort: leads created in last 7 days"
            color="secondary"
          />

          {(() => {
            const den = funnelOverall.newCount;
            const pctConnected = funnelPct(funnelOverall.connectedCount, den);
            const pctVisit = funnelPct(funnelOverall.visitCount, den);
            const pctClosed = funnelPct(funnelOverall.closedCount, den);
            const card = (props: {
              key: string;
              label: string;
              value: React.ReactNode;
              sub: string;
              color: any;
              tooltip?: React.ReactNode;
            }) => {
              const { key, label, value, sub, color, tooltip } = props;
              const body = (
                <Paper
                  key={key}
                  sx={{
                    p: 1.25,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 'none',
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography sx={{ fontSize: 12, fontWeight: 900 }}>{label}</Typography>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: 999,
                        bgcolor: (theme) => alpha((theme as any).palette[color]?.main || theme.palette.primary.main, 0.7),
                      }}
                    />
                  </Stack>
                  <Typography sx={{ fontSize: 20, fontWeight: 900, lineHeight: 1.2 }}>{value}</Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{sub}</Typography>
                </Paper>
              );
              if (!tooltip) return body;
              return (
                <Tooltip key={key} title={tooltip} placement="top" arrow>
                  <Box sx={{ cursor: 'help' }}>{body}</Box>
                </Tooltip>
              );
            };

            const closedTip = (
              <Box sx={{ p: 0.5 }}>
                <Typography sx={{ fontWeight: 900, fontSize: 12, mb: 0.5 }}>Closed Breakdown (cohort)</Typography>
                <Stack spacing={0.25}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ fontSize: 12, opacity: 0.9 }}>Completed</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 800 }}>
                      {funnelOverall.completedCount == null ? '—' : funnelOverall.completedCount}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ fontSize: 12, opacity: 0.9 }}>Other / Unknown</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 800 }}>
                      {funnelOverall.closedCount == null || funnelOverall.completedCount == null
                        ? '—'
                        : Math.max(0, funnelOverall.closedCount - funnelOverall.completedCount)}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            );

            return (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' },
                    gap: 1,
                    mb: 1.25,
                  }}
                >
                  {card({
                    key: 'f_new',
                    label: 'New',
                    value: den ?? <Skeleton width={36} />,
                    sub: '7d cohort',
                    color: 'info',
                  })}
                  {card({
                    key: 'f_connected',
                    label: 'Connected Call %',
                    value: pctConnected == null ? '—' : `${pctConnected}%`,
                    sub:
                      funnelOverall.connectedCount == null || den == null ? '—' : `${funnelOverall.connectedCount}/${den}`,
                    color: 'primary',
                  })}
                  {card({
                    key: 'f_visit',
                    label: 'Visit %',
                    value: pctVisit == null ? '—' : `${pctVisit}%`,
                    sub: funnelOverall.visitCount == null || den == null ? '—' : `${funnelOverall.visitCount}/${den}`,
                    color: 'success',
                  })}
                  {card({
                    key: 'f_closed',
                    label: 'Closed %',
                    value: pctClosed == null ? '—' : `${pctClosed}%`,
                    sub: funnelOverall.closedCount == null || den == null ? '—' : `${funnelOverall.closedCount}/${den}`,
                    color: 'secondary',
                    tooltip: closedTip,
                  })}
                </Box>

                {null}
              </>
            );
          })()}
        </Paper>
      )}

      {isTopAdmin && showSection('source') && sourcePerfRows.length > 0 && (
        <Paper sx={insightPaperSx('success')}>
          <InsightHeader
            icon={<CampaignOutlinedIcon fontSize="small" />}
            title="Source Performance"
            subtitle="New=created 7d • Avg close=closed in 7d • No-show=appointments in 7d"
            color="success"
          />

          <Stack spacing={1.25}>
            <Box>
              <Typography sx={{ fontWeight: 900, fontSize: 12, color: 'text.secondary', mb: 0.75 }}>
                By Source
              </Typography>
              <Stack spacing={0.75}>
                {(() => {
                  const norm = (v: any) => String(v || '').trim().toLowerCase().replace(/\s+/g, ' ');
                  const funnelMap = new Map<string, any>();
                  (funnelBySource || []).forEach((x: any) => funnelMap.set(norm(x.label), x));

                  return sourcePerfRows.slice(0, 6).map((r) => {
                    const f = funnelMap.get(norm(r.label)) || null;
                    const conn = f ? Number(f.connectedCount || 0) : 0;
                    const visit = f ? Number(f.visitCount || 0) : 0;
                    const closed = f ? Number(f.closedCount || 0) : r.closedCount;
                    const den = f ? Number(f.newCount || 0) : r.newCount;
                    const pctConn = funnelPct(conn, den);
                    const pctVisit = funnelPct(visit, den);
                    const pctClosed = funnelPct(closed, den);

                    const metaLine = `Avg close: ${r.avgDaysToClose == null ? '—' : `${r.avgDaysToClose}d`} • No-show: ${
                      r.noShowPct == null ? '—' : `${r.noShowPct}%`
                    }`;

                    const body = (
                      <Paper
                        key={r.key}
                        sx={{
                          p: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          boxShadow: 'none',
                          display: 'flex',
                          gap: 1,
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography sx={{ fontWeight: 800, fontSize: 13, minWidth: 140, pr: 1, flex: '1 1 auto' }}>
                          {r.label}
                        </Typography>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                            New {den} • Conn {conn} ({pctConn == null ? '—' : `${pctConn}%`}) • Visit {visit} (
                            {pctVisit == null ? '—' : `${pctVisit}%`}) • Closed {closed} (
                            {pctClosed == null ? '—' : `${pctClosed}%`})
                          </Typography>
                        </Box>
                      </Paper>
                    );

                    return (
                      <Tooltip key={r.key} title={metaLine} placement="top" arrow>
                        <Box sx={{ cursor: 'help' }}>{body}</Box>
                      </Tooltip>
                    );
                  });
                })()}
              </Stack>
            </Box>

            {null}
          </Stack>
        </Paper>
      )}

      {isTopAdmin && showSection('broker') && (
        <Paper sx={insightPaperSx('info')}>
          <InsightHeader
            icon={<HandshakeOutlinedIcon fontSize="small" />}
            title="Broker Analytics (Agency)"
            subtitle={
              brokerRange === '7d'
                ? 'Last 7 days leads • Expand to see lead list'
                : brokerRange === 'open'
                  ? 'All open (not closed) leads • Expand to see lead list'
                  : 'Last 30 days leads • Expand to see lead list'
            }
            color="info"
            right={
              <Stack direction="row" spacing={0.75}>
                {[
                  { key: '7d', label: '7 days' },
                  { key: '30d', label: '30 days' },
                  { key: 'open', label: 'Open' },
                ].map((opt) => {
                  const selected = brokerRange === (opt.key as any);
                  return (
                    <Button
                      key={opt.key}
                      size="small"
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation();
                        setBrokerRange(opt.key as any);
                        setBrokerExpandedKey(null);
                        setBrokerVisibleCount({});
                      }}
                      sx={{
                        borderRadius: 999,
                        px: 1.25,
                        py: 0.5,
                        minHeight: 30,
                        fontWeight: 900,
                        fontSize: 12,
                        borderColor: selected ? alpha('#0284c7', 0.45) : alpha('#0f172a', 0.14),
                        bgcolor: selected ? alpha('#0284c7', 0.10) : 'rgba(255,255,255,0.75)',
                        color: selected ? '#0369a1' : 'text.primary',
                        '&:hover': { bgcolor: selected ? alpha('#0284c7', 0.14) : 'rgba(255,255,255,0.9)' },
                      }}
                    >
                      {opt.label}
                    </Button>
                  );
                })}
              </Stack>
            }
          />

          <Stack spacing={0.75}>
            {brokerAnalyticsRows.length === 0 && (
              <Typography sx={{ fontSize: 13, color: 'text.secondary', px: 0.25 }}>
                No broker leads found for this filter.
              </Typography>
            )}
            {brokerAnalyticsRows.map((b) => {
              const visible = brokerVisibleCount[b.key] ?? 10;
              const shown = b.leads.slice(0, visible);
              return (
                <Accordion
                  key={b.key}
                  expanded={brokerExpandedKey === b.key}
                  onChange={() => {
                    setBrokerExpandedKey((prev) => (prev === b.key ? null : b.key));
                    setBrokerVisibleCount((prev) => (prev[b.key] ? prev : { ...prev, [b.key]: 10 }));
                  }}
                  disableGutters
                  elevation={0}
                  square
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                    '&:before': { display: 'none' },
                    bgcolor: 'rgba(255,255,255,0.6)',
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'info.main' }} />} sx={{ px: 1.25 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 900, fontSize: 13, minWidth: 0 }} noWrap>
                        {b.label}
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      <Chip
                        size="small"
                        label={b.total}
                        variant="outlined"
                        sx={{ height: 22, '& .MuiChip-label': { fontWeight: 900 } }}
                      />
                      <Typography sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        Closed {b.closed} • Completed {b.completed}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    {shown.length === 0 ? (
                      <Typography sx={{ px: 1.5, py: 1.25, fontSize: 13, color: 'text.secondary' }}>
                        No leads found.
                      </Typography>
                    ) : (
                      <>
                        {shown.map((l: any) => (
                          <LeadCompactCard
                            key={l.id}
                            lead={l}
                            selected={detailLeadId === l.id}
                            onClick={() => openLead(l)}
                            statusLabel={statusLabel(l)}
                            assignedLabel={`${assignedLabel(l)}${String(l?.referredBrokerName || '').trim() ? ` • Broker: ${String(l.referredBrokerName).trim()}` : ''}`}
                            badge={null}
                            view="sheet"
                          />
                        ))}
                        {b.leads.length > shown.length && (
                          <Box sx={{ px: 1.5, py: 1.25 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() =>
                                setBrokerVisibleCount((prev) => ({ ...prev, [b.key]: (prev[b.key] ?? 10) + 10 }))
                              }
                            >
                              Load more
                            </Button>
                          </Box>
                        )}
                      </>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        </Paper>
      )}

      {isTopAdmin && showSection('agent') && agentPerfRows.length > 0 && (
        <Paper sx={insightPaperSx('warning')}>
          <InsightHeader
            icon={<WorkspacePremiumOutlinedIcon fontSize="small" />}
            title="Agent Performance"
            subtitle="Last 7 days activities • Response = first activity after lead created (7d cohort)"
            color="warning"
          />

          <Stack spacing={0.75}>
            {(() => {
              const norm = (v: any) => String(v || '').trim().toLowerCase();
              const perfMap = new Map<string, any>();
              (agentPerfRows || []).forEach((r: any) => perfMap.set(norm(r.key), r));
              const rows = (funnelByAgent || []).slice(0, 10);
              return rows.map((fr: any) => {
                const key = norm(fr.key);
                const pr = perfMap.get(key) || null;
                const den = Number(fr.newCount || 0);
                const conn = Number(fr.connectedCount || 0);
                const visit = Number(fr.visitCount || 0);
                const closed = Number(fr.closedCount || 0);
                const pctConn = funnelPct(conn, den);
                const pctVisit = funnelPct(visit, den);
                const pctClosed = funnelPct(closed, den);
                const overdueShare = overdueShareByAgent.get(key) ?? null;
                const metaLine = `Resp: ${pr?.avgFirstResponseMins == null ? '—' : `${pr.avgFirstResponseMins}m`} • Closed (activities): ${
                  pr?.closures == null ? '—' : pr.closures
                } • Overdue: ${overdueShare == null ? '—' : `${overdueShare}%`}`;

                const body = (
                  <Paper
                    key={fr.key}
                    sx={{
                      p: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: 'none',
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography sx={{ fontWeight: 800, fontSize: 13, minWidth: 160, pr: 1, flex: '1 1 auto' }}>
                      {fr.label}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      New {den} • Conn {conn} ({pctConn == null ? '—' : `${pctConn}%`}) • Visit {visit} (
                      {pctVisit == null ? '—' : `${pctVisit}%`}) • Closed {closed} ({pctClosed == null ? '—' : `${pctClosed}%`})
                    </Typography>
                  </Paper>
                );

                return (
                  <Tooltip key={fr.key} title={metaLine} placement="top" arrow>
                    <Box sx={{ cursor: 'help' }}>{body}</Box>
                  </Tooltip>
                );
              });
            })()}
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
              Overdue% uses the dashboard Due Today/Overdue lists (each limited to 100).
            </Typography>
          </Stack>
        </Paper>
      )}

      {canViewSalesJourney && showSection('sales-journey') && (
        <Paper sx={insightPaperSx('primary')}>
          <InsightHeader
            icon={<AccountTreeOutlinedIcon fontSize="small" />}
            title="Sales Journey Flow"
            subtitle="Assigned leads with status movement in the selected period"
            color="primary"
            right={
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <Select value={journeyRange} onChange={(e) => setJourneyRange(e.target.value as any)}>
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="7d">7 days</MenuItem>
                    <MenuItem value="30d">30 days</MenuItem>
                    <MenuItem value="month">This month</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <Select value={journeySalesperson} onChange={(e) => setJourneySalesperson(String(e.target.value || 'all'))}>
                    <MenuItem value="all">All salespeople</MenuItem>
                    {journeySalespersonOptions.map((u) => (
                      <MenuItem key={u.email} value={u.email}>
                        {u.name || u.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select value={journeySource} onChange={(e) => setJourneySource(String(e.target.value || 'all'))}>
                    <MenuItem value="all">All sources</MenuItem>
                    {journeySourceOptions.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            }
          />

          {journeyError && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {journeyError}
            </Alert>
          )}

          {journeyLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading sales journey…
              </Typography>
            </Box>
          ) : !journeyTree || (journeyTree.leadIds || []).length === 0 ? (
            <Typography sx={{ fontSize: 13, color: 'text.secondary', py: 1 }}>
              No assigned leads with status movement found for this selection.
            </Typography>
          ) : (
            <Stack spacing={1.25}>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  p: 1,
                }}
              >
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ px: 0.5, pb: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 900 }}>
                      Detailed journey paths
                    </Typography>
                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                      <Chip size="small" label={`Assigned ${journeySummary.assigned}`} variant="outlined" />
                      <Chip size="small" label={`First branches ${journeySummary.firstBranches}`} variant="outlined" />
                      <Chip size="small" label={`No movement ${journeySummary.rootNoFurther}`} variant="outlined" />
                      <Chip size="small" label={`Site visited ${journeySiteVisitedCount}`} variant="outlined" />
                      <Chip size="small" label={`Closed reached ${journeySummary.closedReached}`} variant="outlined" />
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      clickable
                      label="Detailed paths"
                      color={journeyViewMode === 'paths' ? 'primary' : 'default'}
                      variant={journeyViewMode === 'paths' ? 'filled' : 'outlined'}
                      onClick={() => setJourneyViewMode('paths')}
                    />
                    <Chip
                      size="small"
                      clickable
                      label="Hierarchy"
                      color={journeyViewMode === 'hierarchy' ? 'primary' : 'default'}
                      variant={journeyViewMode === 'hierarchy' ? 'filled' : 'outlined'}
                      onClick={() => setJourneyViewMode('hierarchy')}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const paths = collectJourneyPaths(journeyTree);
                        setJourneyExpanded(Object.fromEntries(paths.map((p) => [p, true])));
                      }}
                    >
                      Expand all
                    </Button>
                    <Button size="small" onClick={() => setJourneyExpanded({})}>
                      Collapse all
                    </Button>
                  </Stack>
                </Stack>
                {journeyViewMode === 'paths' ? (
                  renderJourneyNode(journeyTree)
                ) : (
                  <Box sx={{ position: 'relative' }}>
                    <Box sx={{ overflowX: 'auto', pb: 0.5 }}>
                      <Box ref={journeyHierarchyCanvasRef} sx={{ position: 'relative', minWidth: { xs: 920, md: 1180 }, pb: 0.5 }}>
                        <svg
                          width={journeyConnectorSize.width}
                          height={journeyConnectorSize.height}
                          viewBox={`0 0 ${journeyConnectorSize.width || 1} ${journeyConnectorSize.height || 1}`}
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            pointerEvents: 'none',
                            overflow: 'visible',
                            zIndex: 0,
                          }}
                          aria-hidden="true"
                        >
                          <defs>
                            {Array.from(new Map(journeyConnectors.map((c) => [c.markerId, c.color])).entries()).map(([markerId, color]) => (
                              <marker
                                key={markerId}
                                id={markerId}
                                markerWidth="9"
                                markerHeight="9"
                                refX="8"
                                refY="4.5"
                                orient="auto"
                                markerUnits="strokeWidth"
                              >
                                <path d="M 0 0 L 9 4.5 L 0 9 z" fill={color} />
                              </marker>
                            ))}
                          </defs>
                          {journeyConnectors.map((c) => (
                            <path
                              key={c.id}
                              d={c.d}
                              fill="none"
                              stroke={c.color}
                              strokeWidth={c.active ? '1.8' : '1.3'}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              markerEnd={`url(#${c.markerId})`}
                              opacity={c.active ? 0.68 : 0.22}
                            />
                          ))}
                        </svg>
                        <Stack spacing={4.5} sx={{ position: 'relative', zIndex: 1 }}>
                          {journeyHierarchyRows.map((row, level) => (
                            <Box key={level}>
                              <Typography sx={{ mb: 1.15, fontSize: 10, fontWeight: 800, color: 'text.secondary', opacity: 0.66, textTransform: 'uppercase', letterSpacing: 0 }}>
                                Level {level}
                              </Typography>
                              <Stack direction="row" spacing={1} alignItems="stretch" justifyContent="center">
                                {row.map((item) => renderJourneyHierarchyCard(item))}
                              </Stack>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    </Box>
                    <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 8, width: 26, pointerEvents: 'none', background: 'linear-gradient(90deg, rgba(255,255,255,0.96), rgba(255,255,255,0))' }} />
                    <Box sx={{ position: 'absolute', right: 0, top: 0, bottom: 8, width: 26, pointerEvents: 'none', background: 'linear-gradient(270deg, rgba(255,255,255,0.96), rgba(255,255,255,0))' }} />
                  </Box>
                )}
              </Box>

              {journeyOpenBranch && (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    overflow: 'hidden',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1.25, py: 1, bgcolor: 'grey.50' }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography noWrap sx={{ fontSize: 13, fontWeight: 900 }}>
                        {journeyOpenBranch.label}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {Math.min(journeyBranchVisible, journeyOpenBranchTotal)}/{journeyOpenBranchTotal} leads
                      </Typography>
                    </Box>
                    <Button size="small" onClick={() => setJourneyOpenBranch(null)}>
                      Close
                    </Button>
                  </Stack>
                  <Divider />
                  <Stack spacing={0} sx={{ p: 1 }}>
                    {journeyOpenBranchLeads.map((l: any) => (
                      <LeadCompactCard
                        key={l.id}
                        lead={l}
                        selected={detailLeadId === l.id}
                        onClick={() => openLead(l)}
                        statusLabel={statusLabel(l)}
                        assignedLabel={assignedLabel(l)}
                        badge={null}
                        view="sheet"
                      />
                    ))}
                  </Stack>
                  {journeyBranchVisible < journeyOpenBranchTotal && (
                    <Box sx={{ px: 1, pb: 1, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button size="small" variant="contained" onClick={() => setJourneyBranchVisible((v) => v + 20)}>
                        Load more
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </Stack>
          )}
        </Paper>
      )}

      {showSection('risk') &&
      (isTopAdmin || isSalesManager) &&
      (riskHighOverdue.length > 0 || riskNoActivityLeads.length > 0 || riskHotNoFollowUpLeads.length > 0) && (
        <Paper sx={insightPaperSx('error')}>
          <InsightHeader
            icon={<WarningAmberOutlinedIcon fontSize="small" />}
            title="Risk Alerts"
            subtitle="Quick issues that need attention"
            color="error"
          />

          <Stack spacing={1.25}>
            <Box>
              <Typography sx={{ fontWeight: 900, fontSize: 12, color: 'text.secondary', mb: 0.75 }}>
                High Overdue (&gt; 7 days) • {riskHighOverdue.length}
              </Typography>
              {riskHighOverdue.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>None</Typography>
              ) : (
                riskHighOverdue.slice(0, 8).map((l) => {
                  const dt = (l as any)?.nextFollowUpDate?.toDate ? (l as any).nextFollowUpDate.toDate() : null;
                  const od = dt ? Math.max(0, daysSince(dt)) : null;
                  return (
                    <LeadCompactCard
                      key={l.id}
                      lead={l}
                      selected={detailLeadId === l.id}
                      onClick={() => openLead(l)}
                      statusLabel={statusLabel(l)}
                      assignedLabel={assignedLabel(l)}
                      badge={od == null ? null : { label: `Overdue ${od}d`, color: 'error' }}
                      view="sheet"
                    />
                  );
                })
              )}
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 900, fontSize: 12, color: 'text.secondary', mb: 0.75 }}>
                No Activity Since • 3d: {riskNoActivityBuckets.d3 == null ? '—' : riskNoActivityBuckets.d3} • 7d:{' '}
                {riskNoActivityBuckets.d7 == null ? '—' : riskNoActivityBuckets.d7} • 14d:{' '}
                {riskNoActivityBuckets.d14 == null ? '—' : riskNoActivityBuckets.d14}
              </Typography>
              {riskNoActivityLeads.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>None</Typography>
              ) : (
                riskNoActivityLeads.slice(0, 8).map((l) => {
                  const ref = (l as any).__staleRef as Date | undefined;
                  const d = ref ? daysSince(ref) : null;
                  return (
                    <LeadCompactCard
                      key={l.id}
                      lead={l}
                      selected={detailLeadId === l.id}
                      onClick={() => openLead(l)}
                      statusLabel={statusLabel(l)}
                      assignedLabel={assignedLabel(l)}
                      badge={d == null ? null : { label: `${d}d`, color: 'warning' }}
                      view="sheet"
                    />
                  );
                })
              )}
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 900, fontSize: 12, color: 'text.secondary', mb: 0.75 }}>
                Hot Leads With No Follow-up • {riskHotNoFollowUpLeads.length}
              </Typography>
              {riskHotNoFollowUpLeads.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>None</Typography>
              ) : (
                riskHotNoFollowUpLeads.slice(0, 8).map((l) => (
                  <LeadCompactCard
                    key={l.id}
                    lead={l}
                    selected={detailLeadId === l.id}
                    onClick={() => openLead(l)}
                    statusLabel={statusLabel(l)}
                    assignedLabel={assignedLabel(l)}
                    badge={{ label: 'No follow-up', color: 'warning' }}
                    view="sheet"
                  />
                ))
              )}
            </Box>
          </Stack>
        </Paper>
      )}

      <Stack direction="column" spacing={2} alignItems="stretch">
        {showSection('my-work') && (
        <Paper sx={{ p: 0, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
          <SectionHeader
            icon={<WorkOutlineOutlinedIcon fontSize="small" />}
            title="My Work"
            subtitle="Due today, overdue, and scheduled"
            color="primary"
          />
          <Divider />

          <WorkAccordion
            icon={<TodayOutlinedIcon sx={{ fontSize: 16 }} />}
            title="Due Today"
            count={countDueToday ?? todayDue.length}
            color="primary"
            expanded={dueTodayOpen}
            onToggle={() => setDueTodayOpen((v) => !v)}
          >
            {countDueToday != null && todayDue.length < countDueToday && (
              <Typography sx={{ px: 1.5, pt: 1.25, fontSize: 12, color: 'text.secondary' }}>
                Showing {todayDue.length} of {countDueToday}.
              </Typography>
            )}
            {todayDue.length === 0 ? (
              <Typography sx={{ px: 1.5, py: 1.25, fontSize: 13, color: 'text.secondary' }}>
                No follow-ups due today.
              </Typography>
            ) : (
              todayDue.map((l) => (
                <LeadCompactCard
                  key={l.id}
                  lead={l}
                  selected={detailLeadId === l.id}
                  onClick={() => openLead(l)}
                  statusLabel={statusLabel(l)}
                  assignedLabel={assignedLabel(l)}
                  badge={null}
                  view="sheet"
                />
              ))
            )}
          </WorkAccordion>

          <WorkAccordion
            icon={<ReportProblemOutlinedIcon sx={{ fontSize: 16 }} />}
            title="Overdue"
            count={countOverdue ?? overdue.length}
            color="error"
            expanded={overdueOpen}
            onToggle={() => setOverdueOpen((v) => !v)}
          >
            {countOverdue != null && overdue.length < countOverdue && (
              <Typography sx={{ px: 1.5, pt: 1.25, fontSize: 12, color: 'text.secondary' }}>
                Showing {overdue.length} of {countOverdue}.
              </Typography>
            )}
            {overdue.length === 0 ? (
              <Typography sx={{ px: 1.5, py: 1.25, fontSize: 13, color: 'text.secondary' }}>
                No overdue leads.
              </Typography>
            ) : (
              overdue.map((l) => (
                <LeadCompactCard
                  key={l.id}
                  lead={l}
                  selected={detailLeadId === l.id}
                  onClick={() => openLead(l)}
                  statusLabel={statusLabel(l)}
                  assignedLabel={assignedLabel(l)}
                  badge={null}
                  view="sheet"
                />
              ))
            )}
          </WorkAccordion>

          <WorkAccordion
            icon={<EventOutlinedIcon sx={{ fontSize: 16 }} />}
            title="Scheduled Today"
            count={scheduledToday.length}
            color="success"
            expanded={scheduledTodayOpen}
            onToggle={() => setScheduledTodayOpen((v) => !v)}
          >
            {scheduledToday.length === 0 ? (
              <Typography sx={{ px: 1.5, py: 1.25, fontSize: 13, color: 'text.secondary' }}>
                No appointments scheduled today.
              </Typography>
            ) : (
              scheduledToday.map((l) => (
                <LeadCompactCard
                  key={l.id}
                  lead={l}
                  selected={detailLeadId === l.id}
                  onClick={() => openLead(l)}
                  statusLabel={statusLabel(l)}
                  assignedLabel={assignedLabel(l)}
                  badge={null}
                  view="sheet"
                />
              ))
            )}
          </WorkAccordion>

          {isTopAdmin && (
            <WorkAccordion
              icon={<GroupsOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Unassigned Leads"
              count={countUnassigned ?? unassignedLeads.length}
              color="warning"
              expanded={unassignedOpen}
              onToggle={() => setUnassignedOpen((v) => !v)}
            >
              {countUnassigned != null && unassignedLeads.length < countUnassigned && (
                <Typography sx={{ px: 1.5, pt: 1.25, fontSize: 12, color: 'text.secondary' }}>
                  Showing {unassignedLeads.length} of {countUnassigned}.
                </Typography>
              )}
              {unassignedLeads.length === 0 ? (
                <Typography sx={{ px: 1.5, py: 1.25, fontSize: 13, color: 'text.secondary' }}>
                  No unassigned leads.
                </Typography>
              ) : (
                unassignedLeads.map((l) => (
                  <LeadCompactCard
                    key={l.id}
                    lead={l}
                    selected={detailLeadId === l.id}
                    onClick={() => openLead(l)}
                    statusLabel={statusLabel(l)}
                    assignedLabel={assignedLabel(l)}
                    badge={null}
                    view="sheet"
                  />
                ))
              )}
            </WorkAccordion>
          )}
        </Paper>
        )}

        {!isSalesTeam && showSection('new-leads') && (
          <Paper sx={{ p: 0, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
            <SectionHeader
              icon={<FiberNewOutlinedIcon fontSize="small" />}
              title="New Leads"
              subtitle="Created today"
              color="info"
            />
            <Divider />
            <WorkAccordion
              icon={<FiberNewOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Created Today"
              count={newToday.length}
              color="info"
              expanded={newLeadsOpen}
              onToggle={() => setNewLeadsOpen((v) => !v)}
            >
              {newToday.length === 0 ? (
                <Typography sx={{ px: 1.5, py: 1.25, fontSize: 13, color: 'text.secondary' }}>
                  No new leads today.
                </Typography>
              ) : (
                newToday.map((l) => (
                  <LeadCompactCard
                    key={l.id}
                    lead={l}
                    selected={detailLeadId === l.id}
                    onClick={() => openLead(l)}
                    statusLabel={statusLabel(l)}
                    assignedLabel={assignedLabel(l)}
                    badge={null}
                    view="sheet"
                  />
                ))
              )}
            </WorkAccordion>
          </Paper>
        )}

        {showSection('assignment-status') && (
          <LeadAssignmentStatusPanel
            roleId={roleId}
            currentUserEmail={String(currentUser?.email || '')}
            userDirectory={userDirectory}
            leadStatuses={leadStatuses}
            onOpenLead={openLead}
            detailLeadId={detailLeadId}
          />
        )}

        {canViewTeamSnapshot && showSection('team') && (
          <Paper sx={{ p: 0, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
            <SectionHeader
              icon={<GroupsOutlinedIcon fontSize="small" />}
              title="Team Snapshot"
              subtitle="Today & overdue by assignee (from the lists above)"
              color="secondary"
            />
            <Divider />
            <Box sx={{ px: 1.5, py: 1.25 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.75 }}>Due Today</Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {Object.keys(teamTodayByAssignee).length === 0 ? (
                  <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>—</Typography>
                ) : (
                  Object.entries(teamTodayByAssignee)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 12)
                    .map(([k, v]) => <Chip key={k} size="small" label={`${k} (${v})`} />)
                )}
              </Stack>
            </Box>
            <Box sx={{ px: 1.5, pb: 1.25 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.75 }}>Overdue</Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {Object.keys(teamOverdueByAssignee).length === 0 ? (
                  <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>—</Typography>
                ) : (
                  Object.entries(teamOverdueByAssignee)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 12)
                    .map(([k, v]) => <Chip key={k} size="small" color="error" label={`${k} (${v})`} />)
                )}
              </Stack>
            </Box>

            <Divider />
            <Box sx={{ px: 1.5, py: 1.25 }}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Work Done</Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                      Logged activities (calls / visits / internal)
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      label="Today"
                      color={teamRange === 'today' ? 'primary' : 'default'}
                      onClick={() => setTeamRange('today')}
                      variant={teamRange === 'today' ? 'filled' : 'outlined'}
                    />
                    <Chip
                      size="small"
                      label="Yesterday"
                      color={teamRange === 'yesterday' ? 'primary' : 'default'}
                      onClick={() => setTeamRange('yesterday')}
                      variant={teamRange === 'yesterday' ? 'filled' : 'outlined'}
                    />
                    <Chip
                      size="small"
                      label="7 days"
                      color={teamRange === '7d' ? 'primary' : 'default'}
                      onClick={() => setTeamRange('7d')}
                    variant={teamRange === '7d' ? 'filled' : 'outlined'}
                  />
                </Stack>
              </Stack>
            </Box>
            <Divider />

            <Box sx={{ px: 1.5, pb: 1.25 }}>
              {teamError && (
                <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
                  {teamError}
                </Alert>
              )}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.75, color: 'text.secondary' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700 }}>User</Typography>
                </Box>
                <Typography sx={{ width: 74, textAlign: 'right', fontSize: 12, fontWeight: 700 }}>Calls</Typography>
                <Typography sx={{ width: 70, textAlign: 'right', fontSize: 12, fontWeight: 700 }}>Connected</Typography>
                <Typography sx={{ width: 60, textAlign: 'right', fontSize: 12, fontWeight: 700 }}>Visits</Typography>
                <Typography sx={{ width: 72, textAlign: 'right', fontSize: 12, fontWeight: 700 }}>Internal</Typography>
                <Typography sx={{ width: 70, textAlign: 'right', fontSize: 12, fontWeight: 700 }}>Leads</Typography>
              </Stack>
              <Divider />
              {teamLoading ? (
                <Typography sx={{ py: 1.25, fontSize: 13, color: 'text.secondary' }}>Loading…</Typography>
              ) : teamStats.length === 0 ? (
                <Typography sx={{ py: 1.25, fontSize: 13, color: 'text.secondary' }}>No activity logged.</Typography>
              ) : (
                teamStats.slice(0, 20).map((r) => (
                  <Box key={r.key}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{
                        py: 0.85,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.03)' },
                      }}
                      onClick={() => {
                        // Keep only one accordion open at a time in Team Snapshot.
                        setTeamMetricOpen(null);
                        setWorkDoneVisible(10);
                        setWorkDoneUserKey((prev) => {
                          if (prev === r.key) {
                            setWorkDoneExpanded((e) => !e);
                            return prev;
                          }
                          setWorkDoneExpanded(true);
                          return r.key;
                        });
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setTeamMetricOpen(null);
                          setWorkDoneVisible(10);
                          setWorkDoneUserKey((prev) => {
                            if (prev === r.key) {
                              setWorkDoneExpanded((x) => !x);
                              return prev;
                            }
                            setWorkDoneExpanded(true);
                            return r.key;
                          });
                        }
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }}>
                          {r.name}
                        </Typography>
                      </Box>
                      {(
                        [
                          ['callsTotal', r.callsTotal, 74],
                          ['callsConnected', r.callsConnected, 70],
                          ['visits', r.visits, 60],
                          ['internal', r.internal, 72],
                          ['leadsTouched', r.leadsTouched, 70],
                        ] as Array<[string, number, number]>
                      ).map(([metric, value, width]) => (
                        <ButtonBase
                          key={metric}
                          onClick={(e) => {
                            e.stopPropagation();
                            // Keep only one accordion open at a time in Team Snapshot.
                            setWorkDoneExpanded(false);
                            setWorkDoneUserKey(null);
                            setTeamMetricVisible(15);
                            setTeamMetricOpen((prev) =>
                              prev && prev.userKey === r.key && prev.metric === metric ? null : { userKey: r.key, metric }
                            );
                          }}
                          sx={{
                            width,
                            justifyContent: 'flex-end',
                            borderRadius: 1,
                            '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) },
                          }}
                        >
                          <Typography sx={{ textAlign: 'right', fontSize: 13, color: value > 0 ? 'primary.main' : 'text.primary' }}>
                            {value}
                          </Typography>
                        </ButtonBase>
                      ))}
                    </Stack>

                    <Collapse in={workDoneExpanded && workDoneUserKey === r.key} timeout="auto" unmountOnExit>
                      <InlineWorkDoneDetails userKey={r.key} />
                    </Collapse>
                    <Collapse
                      in={!!teamMetricOpen && teamMetricOpen.userKey === r.key}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box sx={{ px: 1.5, pb: 1.5, pt: 0.75, bgcolor: 'grey.50' }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 900 }} noWrap>
                            {teamMetricLabel} leads — {teamMetricUserName}
                          </Typography>
                          <Box sx={{ flex: 1 }} />
                          <Chip
                            size="small"
                            label={`${Math.min(teamMetricVisible, teamMetricTotal)}/${teamMetricTotal}`}
                            variant="outlined"
                            sx={{ height: 22, '& .MuiChip-label': { fontWeight: 800, fontSize: 11 } }}
                          />
                        </Stack>
                        {teamMetricRows.length === 0 ? (
                          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>No leads.</Typography>
                        ) : (
                          <Stack spacing={0}>
                            {teamMetricRows.map((l: any) => (
                              <LeadCompactCard
                                key={l.id}
                                lead={l}
                                selected={detailLeadId === l.id}
                                onClick={() => openLead(l)}
                                statusLabel={statusLabel(l)}
                                assignedLabel={assignedLabel(l)}
                                badge={null}
                                view="sheet"
                              />
                            ))}
                          </Stack>
                        )}
                        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
                          <Button size="small" onClick={() => setTeamMetricOpen(null)}>
                            Close
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={teamMetricVisible >= teamMetricTotal}
                            onClick={() => setTeamMetricVisible((v) => v + 15)}
                          >
                            Load more
                          </Button>
                        </Stack>
                      </Box>
                    </Collapse>
                  </Box>
                ))
              )}
            </Box>
          </Paper>
        )}
      </Stack>

          </Box>
        );
      })()}

      <AddLeadDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} onCreated={() => {}} />
      <LeadDetailDrawer
        open={detailDrawerOpen}
        lead={detailLeadFallback}
        onClose={closeLead}
        onExited={handleLeadDrawerExited}
        onDeleted={() => closeLead()}
        onUpdated={(partial) => {
          // Keep the open drawer content snappy even if page lists are paginated elsewhere.
          setDetailLeadFallback((prev: any) => (prev && prev.id === detailLeadId ? { ...prev, ...partial } : prev));
        }}
        onWidthChange={(w) => {
          const minList = 340;
          const maxDrawer = 700;
          const minDrawer = 320;
          const viewport = typeof window !== 'undefined' ? window.innerWidth : 1200;
          const maxAllowed = Math.max(minDrawer, Math.min(maxDrawer, viewport - minList));
          const clamped = Math.min(maxAllowed, Math.max(minDrawer, w || minDrawer));
          setDetailWidth(clamped);
        }}
        onResizingChange={() => {}}
        mobileFullScreen
      />
    </>
  );
}
